import { useState, useEffect } from "react";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  // Theatrical depths — dark cinema walls, gradient-ready
  navy: "#04080F",
  navyMid: "#0A1220",
  navyLight: "#111E36",
  navyCard: "#0C1628",
  navyGlass: "rgba(4,8,15,0.92)",
  // Screen glow — warm amber-gold, like projector light
  gold: "#C9A84C",
  goldLight: "#E4C566",
  goldDim: "rgba(201,168,76,0.10)",
  goldBorder: "rgba(201,168,76,0.22)",
  goldGlow: "rgba(201,168,76,0.08)",
  // Warm white hierarchy — slightly cream, not clinical
  white: "#F0EBE0",
  offwhite: "#B5B0A5",
  grey: "#8A94A8",
  greyDim: "#1C2B42",
  // Semantic
  red: "#E04B3D",
  green: "#27AE60",
  // Atmosphere — glass with cinematic warmth
  glass: "rgba(255,255,255,0.025)",
  glassBorder: "rgba(255,255,255,0.055)",
};

const FontStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400&family=Outfit:wght@300;400;500;600;700&display=swap');

    *{box-sizing:border-box;margin:0;padding:0;}
    html,body{background:#020408;font-family:'Outfit',sans-serif;color:${T.white};-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;}

    ::-webkit-scrollbar{width:3px;height:3px;}
    ::-webkit-scrollbar-track{background:transparent;}
    ::-webkit-scrollbar-thumb{background:${T.goldBorder};border-radius:2px;}

    /* ── Animations ── */
    @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes scaleIn{from{transform:scale(0.88);opacity:0}to{transform:scale(1);opacity:1}}
    @keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
    @keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
    @keyframes dotPop{0%,100%{transform:scale(1)}50%{transform:scale(1.4)}}
    @keyframes glowPulse{0%,100%{box-shadow:0 0 20px rgba(201,168,76,.2),0 0 40px rgba(201,168,76,.05)}50%{box-shadow:0 0 28px rgba(201,168,76,.3),0 0 56px rgba(201,168,76,.1)}}
    @keyframes seatPulse{0%,100%{box-shadow:0 0 0 0 rgba(201,168,76,.4)}70%{box-shadow:0 0 0 8px rgba(201,168,76,0)}}
    @keyframes grainDrift{0%{transform:translate(0,0)}10%{transform:translate(-1%,-1%)}20%{transform:translate(1%,0.5%)}30%{transform:translate(-0.5%,1%)}40%{transform:translate(0.5%,-0.5%)}50%{transform:translate(-1%,0.5%)}60%{transform:translate(1%,-1%)}70%{transform:translate(-0.5%,-0.5%)}80%{transform:translate(0.5%,1%)}90%{transform:translate(-1%,0.5%)}100%{transform:translate(0,0)}}

    /* ── Screen transitions ── */
    .screen{animation:fadeUp .38s cubic-bezier(.22,1,.36,1) both;flex:1;min-height:0;overflow-y:auto;}
    .fade{animation:fadeIn .24s ease both;}

    /* ── Interactions ── */
    .press{transition:transform .14s ease,opacity .14s ease;cursor:pointer;}
    .press:active{transform:scale(.96);opacity:.85;}
    .card-lift{transition:transform .22s cubic-bezier(.22,1,.36,1),box-shadow .22s ease;cursor:pointer;}
    .card-lift:hover{transform:translateY(-3px);box-shadow:0 16px 44px rgba(0,0,0,.65),0 0 0 1px ${T.goldGlow};}

    /* ── Buttons — luminous CTAs ── */
    .btn-gold{
      background:linear-gradient(135deg,${T.gold} 0%,${T.goldLight} 100%);
      color:#04080F;border:none;border-radius:10px;
      font-family:'Outfit',sans-serif;font-weight:700;letter-spacing:.01em;
      cursor:pointer;transition:all .22s ease;
      box-shadow:0 4px 20px rgba(201,168,76,.25),0 1px 0 rgba(255,255,255,.15) inset;
    }
    .btn-gold:hover{filter:brightness(1.1);transform:translateY(-1px);box-shadow:0 8px 32px rgba(201,168,76,.35),0 1px 0 rgba(255,255,255,.15) inset;}
    .btn-gold:active{transform:scale(.97);box-shadow:0 2px 12px rgba(201,168,76,.2);}

    .btn-outline{
      background:transparent;color:${T.gold};
      border:1.5px solid ${T.goldBorder};border-radius:10px;
      font-family:'Outfit',sans-serif;font-weight:500;
      cursor:pointer;transition:all .22s ease;
    }
    .btn-outline:hover{background:${T.goldDim};border-color:${T.gold};box-shadow:0 0 20px rgba(201,168,76,.08);}

    /* ── Glass surfaces ── */
    .glass-card{
      background:linear-gradient(145deg,rgba(255,255,255,0.03),rgba(255,255,255,0.012));
      border:1px solid ${T.glassBorder};
      backdrop-filter:blur(16px);border-radius:14px;
    }

    /* ── Tabs ── */
    .tab-on{color:${T.gold};border-bottom:2px solid ${T.gold};}
    .tab-off{color:${T.grey};border-bottom:2px solid transparent;transition:color .2s;}
    .tab-off:hover{color:${T.offwhite};}

    /* ── Tags & inputs ── */
    .tag{background:${T.goldDim};color:${T.goldLight};border:1px solid ${T.goldBorder};border-radius:5px;font-size:10px;font-weight:700;padding:2px 7px;letter-spacing:.04em;}
    .inp{
      background:${T.navyLight};border:1px solid ${T.greyDim};border-radius:10px;
      color:${T.white};font-family:'Outfit',sans-serif;padding:11px 14px;font-size:14px;
      outline:none;width:100%;transition:border-color .2s,box-shadow .2s;
    }
    .inp:focus{border-color:${T.gold};box-shadow:0 0 0 3px rgba(201,168,76,.08);}

    /* ── Horizontal scroll, labels, pills ── */
    .hscroll{display:flex;gap:12px;overflow-x:auto;padding-bottom:4px;scrollbar-width:none;}
    .hscroll::-webkit-scrollbar{display:none;}
    .section-label{font-size:16px;font-weight:700;color:${T.white};letter-spacing:.01em;}
    .view-all{font-size:12px;font-weight:600;color:${T.gold};background:none;border:none;cursor:pointer;font-family:'Outfit',sans-serif;transition:color .18s;}
    .view-all:hover{color:${T.goldLight};}

    .pill-time{
      background:transparent;border:1.5px solid ${T.greyDim};border-radius:50px;
      color:${T.white};padding:8px 16px;font-size:13px;font-weight:600;
      font-family:'Outfit',sans-serif;cursor:pointer;transition:all .2s;
    }
    .pill-time:hover{border-color:${T.gold};color:${T.gold};box-shadow:0 0 16px rgba(201,168,76,.08);}
    .pill-time.selected{background:${T.gold};border-color:${T.gold};color:#04080F;box-shadow:0 4px 16px rgba(201,168,76,.3);}
  `}</style>
);

// ─── DATA ─────────────────────────────────────────────────────────────────────
const MOVIES = [
  { id:1, title:"Wuthering Heights", genre:"Romance", rating:"R", runtime:"2h 16m", score:6.3, rtFresh:58, rtAudience:84, year:2026, desc:"Emerald Fennell's visceral reimagining of Emily Brontë's gothic classic, following the tempestuous and destructive bond between Catherine Earnshaw and Heathcliff across the Yorkshire moors.", color:"#1a080e", poster:"🌿", cast:["Margot Robbie","Jacob Elordi","Hong Chau","Shazad Latif"], director:"Emerald Fennell" },
  { id:2, title:"Hoppers", genre:"Animation", rating:"PG", runtime:"1h 44m", score:7.7, rtFresh:94, rtAudience:94, year:2026, desc:"Teenage animal-lover Mabel uses new technology to hop her consciousness into a robotic beaver — and discovers a hidden world of animal secrets that puts her at odds with powerful human forces.", color:"#0a1e0a", poster:"🦫", cast:["Piper Curda","Bobby Moynihan","Jon Hamm","Meryl Streep"], director:"Daniel Chong" },
  { id:3, title:"The Bride!", genre:"Horror", rating:"R", runtime:"2h 6m", score:6.1, rtFresh:59, rtAudience:50, year:2026, desc:"In 1930s Chicago, Frankenstein's Monster persuades an eccentric scientist to resurrect a murdered young woman as his companion — but The Bride has ambitions that go far beyond what either man imagined.", color:"#1a0624", poster:"⚡", cast:["Jessie Buckley","Christian Bale","Annette Bening","Penélope Cruz"], director:"Maggie Gyllenhaal" },
  { id:4, title:"Peaky Blinders: The Immortal Man", genre:"Crime", rating:"R", runtime:"1h 52m", score:7.6, rtFresh:93, rtAudience:94, year:2026, desc:"Self-exiled gangster Tommy Shelby is dragged back to Birmingham when his estranged son becomes entangled in a dangerous Nazi conspiracy, forcing one final reckoning with his past.", color:"#100e08", poster:"🎩", cast:["Cillian Murphy","Barry Keoghan","Rebecca Ferguson","Tim Roth"], director:"Tom Harper" },
  { id:5, title:"undertone", genre:"Horror", rating:"R", runtime:"1h 33m", score:6.5, rtFresh:88, rtAudience:76, year:2026, desc:"A skeptical paranormal podcaster moves home to care for her terminally ill mother and becomes consumed by chilling audio recordings sent by a couple experiencing unexplained phenomena in their home.", color:"#080810", poster:"🎙️", cast:["Nina Kiri","Adam DiMarco","Ari Millen"], director:"Ian Tuason" },
  { id:6, title:"Reminders of Him", genre:"Romance", rating:"PG-13", runtime:"1h 54m", score:7.2, rtFresh:69, rtAudience:84, year:2026, desc:"After serving time for an accident that took her partner's life, Kenna Rowan returns to her Wyoming hometown hoping to reunite with the young daughter she's never known — but the community won't forgive.", color:"#200a0a", poster:"💌", cast:["Maika Monroe","Tyriq Withers","Rudy Pankow","Lauren Graham"], director:"Vanessa Caswill" },
];
const COMING_SOON = [
  { id:7, title:"Project Hail Mary", genre:"Sci-Fi", rating:"PG-13", runtime:"2h 36m", rtFresh:null, year:2026, desc:"A science teacher wakes alone on a spacecraft light-years from Earth with no memory of who he is or how he got there — and must solve an impossible mystery to save humanity.", color:"#04081a", poster:"🚀", releaseDate:"Mar 20" },
  { id:8, title:"Ready or Not 2: Here I Come", genre:"Thriller", rating:"R", runtime:"1h 48m", rtFresh:null, year:2026, desc:"Moments after surviving the Le Domas family's deadly game, Grace discovers she's been thrust into a global hunt by the world's most powerful dynasties — with her estranged sister now caught in the crossfire.", color:"#1a0408", poster:"🃏", releaseDate:"Mar 20" },
  { id:9, title:"They Will Kill You", genre:"Action", rating:"R", runtime:"1h 42m", rtFresh:null, year:2026, desc:"A new housekeeper at a mysterious New York City high-rise discovers that her wealthy employers are members of a murderous Satanic cult — and she's already their next target.", color:"#1a0804", poster:"🗡️", releaseDate:"Mar 27" },
];
const THEATERS = [
  { id:1, name:"ReelView Grand Cineplex", short:"Grand Cineplex", address:"450 Cinema Blvd, Downtown", city:"Los Angeles, CA 90012", phone:"(213) 555-0182", distance:"1.2 mi", screens:14, amenities:["IMAX","Dolby Atmos","Recliner Seats"] },
  { id:2, name:"ReelView Westside 10", short:"Westside 10", address:"1820 Sunset Pkwy", city:"Los Angeles, CA 90025", phone:"(310) 555-0247", distance:"3.8 mi", screens:10, amenities:["Dolby Atmos","Recliner Seats","Bar & Lounge"] },
  { id:3, name:"ReelView Marina Bay", short:"Marina Bay", address:"750 Harbor View Dr", city:"Los Angeles, CA 90292", phone:"(310) 555-0391", distance:"6.1 mi", screens:8, amenities:["Dolby Atmos","Recliner Seats"] },
];
// Pricing per format (adult base) — fee is baked in for display
const FORMAT_PRICING = { "IMAX":21.49, "Dolby Atmos":19.49, "Standard":16.50 };
const SERVICE_FEE_GLOBAL = 2.19;
const MATINEE_CUTOFF = "4:00 PM"; // Shows before this get matinee discount
const MATINEE_DISCOUNT = 3.00;
const isMatinee = t => { const h = parseInt(t); const pm = t.includes("PM"); return (!pm || h === 12) && !(pm && h !== 12); };

// Showroom types — different sizes/experiences per format & theater
const SHOWROOMS = {
  "IMAX": { name:"IMAX Grand Auditorium", seats:320, tag:"Large Format" },
  "Dolby Atmos": { name:"Dolby Cinema", seats:210, tag:"Premium Sound" },
  "Standard-L": { name:"Auditorium 1", seats:280, tag:"Large" },
  "Standard-M": { name:"Auditorium 5", seats:160, tag:"Medium" },
  "Standard-S": { name:"Screening Room 3", seats:85, tag:"Intimate" },
};
const SHOWTIMES_DATA = {
  1: { formats:[
    { fmt:"IMAX", tagline:"PREMIUM IMMERSION", amenities:"Heated Recliners · Reserved Seating · Closed Caption · Audio Description",
      showroom:"IMAX",
      times:["10:15 AM","1:30 PM","4:45 PM","8:00 PM","11:00 PM"],
      seatsLeft:{  "8:00 PM":12, "11:00 PM":38 } },
    { fmt:"Dolby Atmos", tagline:"3D AUDIO", amenities:"Heated Recliners · Dine-In Delivery · Reserved Seating · Closed Caption",
      showroom:"Dolby Atmos",
      times:["12:00 PM","3:20 PM","6:50 PM","10:10 PM"],
      seatsLeft:{ "6:50 PM":8 } },
    { fmt:"Standard", tagline:"", amenities:"Heated Recliners · Reserved Seating · Closed Caption",
      showroom:"Standard-L",
      times:["11:00 AM","2:00 PM","5:15 PM","8:30 PM"] },
  ]},
  2: { formats:[
    { fmt:"Dolby Atmos", tagline:"3D AUDIO", amenities:"Heated Recliners · Reserved Seating · Closed Caption",
      showroom:"Dolby Atmos",
      times:["2:30 PM","5:45 PM","9:00 PM"],
      seatsLeft:{ "9:00 PM":15 } },
    { fmt:"Standard", tagline:"", amenities:"Recliner Seats · Reserved Seating",
      showroom:"Standard-M",
      times:["1:15 PM","4:20 PM","7:30 PM","10:45 PM"] },
  ]},
  3: { formats:[
    { fmt:"Standard", tagline:"", amenities:"Recliner Seats · Reserved Seating · Audio Description",
      showroom:"Standard-S",
      times:["12:30 PM","3:45 PM","7:00 PM"] },
  ]},
};
const CONCESSION_IMAGES = {
  1: "https://images.unsplash.com/photo-1585647347483-22b66260dfff?w=200&h=200&fit=crop",  // Popcorn
  2: "https://images.unsplash.com/photo-1505686994434-e3cc5abf1330?w=200&h=200&fit=crop",  // Combo meal — popcorn bucket with drink
  3: "https://images.unsplash.com/photo-1513456852971-30c0b8199d4d?w=200&h=200&fit=crop",  // Nachos
  5: "https://images.unsplash.com/photo-1619740455993-9e612b1af08a?w=200&h=200&fit=crop",  // Hotdog
  6: "https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=200&h=200&fit=crop",  // Candy assortment
  7: "https://images.unsplash.com/photo-1622483767028-3f66f32aef97?w=200&h=200&fit=crop",  // Fountain drink / soda cup
  8: "https://images.unsplash.com/photo-1616118132534-381148898bb4?w=200&h=200&fit=crop",  // Plastic water bottle
};
const CONCESSIONS = [
  { id:1, name:"Large Popcorn", price:9.50, emoji:"🍿", cat:"Snacks" },
  { id:2, name:"Combo Meal", price:16.00, emoji:"🥤", cat:"Combos" },
  { id:3, name:"Nachos & Cheese", price:8.75, emoji:"🧀", cat:"Snacks" },
  { id:5, name:"Hotdog", price:7.25, emoji:"🌭", cat:"Snacks" },
  { id:6, name:"Candy Box", price:5.00, emoji:"🍬", cat:"Snacks" },
  { id:7, name:"Fountain Drink", price:5.50, emoji:"🥤", cat:"Drinks" },
  { id:8, name:"Bottled Water", price:3.50, emoji:"💧", cat:"Drinks" },
];

// ─── LOGO ─────────────────────────────────────────────────────────────────────
const Logo = ({ size=1 }) => (
  <div style={{display:"flex",alignItems:"center",gap:7*size,userSelect:"none"}}>
    <svg width={26*size} height={26*size} viewBox="0 0 26 26" fill="none">
      {/* Outer reel ring */}
      <circle cx="13" cy="13" r="12" stroke={T.gold} strokeWidth="1.5"/>
      {/* Center hub */}
      <circle cx="13" cy="13" r="3.2" fill={T.gold} opacity=".9"/>
      <circle cx="13" cy="13" r="1.6" fill={T.navy}/>
      {/* Sprocket holes — evenly spaced around the reel */}
      <circle cx="13" cy="5.2" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="19.6" cy="9.1" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="19.6" cy="16.9" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="13" cy="20.8" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="6.4" cy="16.9" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      <circle cx="6.4" cy="9.1" r="1.8" fill={T.navy} stroke={T.gold} strokeWidth="1"/>
      {/* Spokes from hub to sprockets */}
      <path d="M13 6.4V9.8M13 16.2v3.4M17.7 10.3l-2.9 1.7M11.2 14l-2.9 1.7M17.7 15.7l-2.9-1.7M11.2 12l-2.9-1.7" stroke={T.gold} strokeWidth=".8" opacity=".5"/>
    </svg>
    <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:17*size,color:T.white,letterSpacing:".02em"}}>
      Reel<span style={{color:T.gold}}>View</span>
    </span>
  </div>
);

// ─── BOTTOM NAV ───────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  {id:"movies",label:"Movies",icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 4v16M16 4v16M2 9h4M18 9h4M2 15h4M18 15h4"/></svg>},
  {id:"theaters",label:"Theaters",icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>},
  {id:"concessions",label:"Concessions",icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22"><path d="M8 22h8l1-12H7l1 12z"/><path d="M7 10c0-1.5.8-3 2-3.5C9.2 5 10.5 4 12 4s2.8 1 3 2.5c1.2.5 2 2 2 3.5"/><circle cx="10" cy="6.5" r="1.5"/><circle cx="14" cy="5.5" r="1.5"/><circle cx="12" cy="8" r="1.5"/></svg>},
  {id:"myreelview",label:"My ReelView",icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>},
];

const BottomNav = ({ active, nav }) => (
  <div style={{width:"100%",background:T.navyGlass,backdropFilter:"blur(24px)",borderTop:`1px solid ${T.glassBorder}`,display:"flex",flexShrink:0,zIndex:200}}>
    {NAV_ITEMS.map(it => {
      const on = active === it.id;
      return (
        <button key={it.id} onClick={()=>nav(it.id)} style={{flex:1,padding:"9px 0 12px",background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
          <span style={{color:on?T.gold:T.grey,transition:"color .2s"}}>{it.icon}</span>
          <span style={{fontSize:9,fontWeight:on?700:500,color:on?T.gold:T.grey,letterSpacing:".04em",textTransform:"uppercase",transition:"color .2s"}}>{it.label}</span>
          {on&&<div style={{width:16,height:2,background:T.gold,borderRadius:1,marginTop:1}}/>}
        </button>
      );
    })}
  </div>
);

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const QuestionIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{display:"inline",verticalAlign:"middle",marginLeft:3,cursor:"help"}}>
    <circle cx="7" cy="7" r="6.3" stroke={T.grey} strokeWidth="1.2"/>
    <text x="7" y="11" textAnchor="middle" fill={T.grey} fontSize="9" fontFamily="DM Sans" fontWeight="700">?</text>
  </svg>
);

const RTScore = ({ pct, type }) => (
  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${T.glassBorder}`}}>
    <div style={{width:36,height:36,borderRadius:"50%",background:type==="fresh"?"rgba(201,168,76,.15)":"rgba(255,100,50,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
      {type==="fresh"?"🍅":"🍿"}
    </div>
    <div>
      <div style={{fontSize:16,fontWeight:700,color:T.white}}>{pct}%</div>
      <div style={{fontSize:11,color:T.grey}}>{type==="fresh"?"Tomatometer":"Audience Score"}</div>
    </div>
  </div>
);

// ─── MOVIE POSTERS — Official TMDb poster images ─────────────────────────────
const POSTER_IMAGES = {
  1: "https://image.tmdb.org/t/p/w500/3YBce6dTh1D5oCMITXk2S5QhPt.jpg",   // Wuthering Heights
  2: "https://image.tmdb.org/t/p/w500/xjtWQ2CL1mpmMNwuU5HeS4Iuwuu.jpg",   // Hoppers
  3: "https://image.tmdb.org/t/p/w500/lV8YHwGkYZsm6EfIqnhaSz2avKt.jpg",   // The Bride!
  4: "https://image.tmdb.org/t/p/w500/gRMalasZEzsZi4w2VFuYusfSfqf.jpg",   // Peaky Blinders: The Immortal Man
  5: "https://image.tmdb.org/t/p/w500/1wREWUEHzjDWSreNFbFLDO7YEaM.jpg",   // undertone
  6: "https://image.tmdb.org/t/p/w500/8hEBaGD2sx4uVe4A1E44NkFIrDm.jpg",   // Reminders of Him
  7: "https://image.tmdb.org/t/p/w500/vOhW7Lbt4kZAqe0vJhVC4Ced9x2.jpg",   // Project Hail Mary
  8: "https://image.tmdb.org/t/p/w500/13ZcJzSGEqVgDSqsS9U5EkQwPkV.jpg",   // Ready or Not 2
  9: "https://image.tmdb.org/t/p/w500/j1yZpgJzjDCbXyA7voSC7SEhcZY.jpg",   // They Will Kill You
};
const POSTER_COLORS = {
  1:"#1a080e",  // Wuthering Heights — dark moors
  2:"#0a1e0a",  // Hoppers — forest green
  3:"#1a0624",  // The Bride! — gothic purple
  4:"#100e08",  // Peaky Blinders — industrial shadow
  5:"#080810",  // undertone — near black
  6:"#200a0a",  // Reminders of Him — warm deep red
  7:"#04081a",  // Project Hail Mary — deep space
  8:"#1a0408",  // Ready or Not 2 — dark blood red
  9:"#1a0804",  // They Will Kill You — dark ember
};
// ─── HERO BACKDROP IMAGES — reuse poster images displayed as cinematic backdrops ──
const MoviePoster = ({ movieId, width=150, height=215, mini=false }) => {
  const src = POSTER_IMAGES[movieId];
  const fallbackBg = POSTER_COLORS[movieId] || "#080D1A";
  return (
    <div style={{width,height,borderRadius:mini?7:10,overflow:"hidden",position:"relative",background:fallbackBg}}>
      {src && <img src={src} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}} loading="lazy"/>}
      {/* Subtle cinematic vignette — preserves the official poster art */}
      <div style={{position:"absolute",inset:0,background:`linear-gradient(180deg,rgba(0,0,0,.08) 0%,transparent 35%,rgba(0,0,0,.18) 100%)`}}/>
      <div style={{position:"absolute",inset:0,background:`linear-gradient(145deg,${fallbackBg}22,transparent 50%)`}}/>
    </div>
  );
};

// Seat preview component — per-showtime availability
const InlineSeatPreview = ({ showtimeKey, times, seatsLeft }) => {
  const [selTime, setSelTime] = useState(times?.[0] || null);
  const PREV_ROWS = ["A","B","C","D","E","F","G","H","I","J","K"];
  const PREV_COLS = 12;
  const ACC_ROW = "D";
  const PREM_ROWS = ["E","F","G"];
  const sectionAt = r => r==="A"?"FRONT":r==="E"?"CENTER":r==="H"?"REAR":null;

  // Deterministic pseudo-random per showtime (hash-based for even distribution)
  const hashStr = s => { let h=0; for(let i=0;i<s.length;i++) h=((h<<5)-h)+s.charCodeAt(i)|0; return Math.abs(h); };
  const seed = hashStr(`${showtimeKey}-${selTime}`);
  const prng = i => { let h=(seed+i*374761393)|0; h=Math.imul(h^(h>>>15),0x1a85ec53); h=Math.imul(h^(h>>>13),0xc4ae3d47); return ((h^(h>>>16))>>>0)/4294967296; };

  const allSeats = PREV_ROWS.flatMap(r=>Array.from({length:PREV_COLS},(_,i)=>`${r}${i+1}`));
  const left = seatsLeft?.[selTime];
  // Popular showtimes fill more seats
  const fillPct = left!=null ? Math.min(0.82, Math.max(0.25, 1-left/(PREV_ROWS.length*PREV_COLS))) : 0.12+prng(999)*0.28;
  const TAKEN_SET = new Set();
  allSeats.forEach((s,i) => { if(prng(i+1)<fillPct) TAKEN_SET.add(s); });
  const available = allSeats.length - TAKEN_SET.size;

  return (
    <div style={{background:T.navyMid,borderRadius:12,padding:"12px 8px 10px",marginTop:10,animation:"fadeIn .2s ease"}}>
      {/* Showtime selector */}
      {times&&times.length>1&&(
        <div style={{display:"flex",gap:4,flexWrap:"wrap",padding:"0 4px",marginBottom:8}}>
          {times.map(t=>{
            const on=selTime===t;
            const tLeft=seatsLeft?.[t];
            return(
              <button key={t} onClick={()=>setSelTime(t)} style={{padding:"4px 10px",borderRadius:6,fontSize:10,fontWeight:on?700:500,background:on?T.goldDim:"transparent",border:`1px solid ${on?T.goldBorder:T.greyDim}`,color:on?T.goldLight:T.grey,cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all .15s",display:"flex",alignItems:"center",gap:3}}>
                {t}
                {tLeft!=null&&tLeft<=20&&<span style={{width:4,height:4,borderRadius:"50%",background:tLeft<=10?T.red:"rgba(255,160,0,.7)"}}/>}
              </button>
            );
          })}
        </div>
      )}

      {/* Availability summary */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 6px",marginBottom:6}}>
        <span style={{fontSize:8,color:T.grey}}>{available} of {allSeats.length} seats available</span>
        {left!=null&&left<=20&&<span style={{fontSize:8,color:T.red,fontWeight:700}}>{left<=10?"Almost sold out!":"Filling fast"}</span>}
      </div>

      {/* Screen arc */}
      <div style={{textAlign:"center",marginBottom:6}}>
        <svg width="200" height="12" viewBox="0 0 200 12" fill="none" style={{display:"block",margin:"0 auto"}}>
          <path d="M4 10 Q100 1 196 10" stroke={T.gold} strokeWidth="1.5" fill="none" opacity=".6" strokeLinecap="round"/>
        </svg>
        <div style={{fontSize:7,color:T.grey,letterSpacing:".14em",marginTop:1}}>SCREEN</div>
      </div>

      {/* Seat grid with section labels */}
      <div style={{display:"flex",flexDirection:"column",gap:0,alignItems:"center"}}>
        {PREV_ROWS.flatMap(row => {
          const sec = sectionAt(row);
          const isPrem = PREM_ROWS.includes(row);
          const isAcc = row === ACC_ROW;
          const seatRow = (
            <div key={row} style={{display:"flex",alignItems:"center",gap:1,marginBottom:2}}>
              <span style={{fontSize:6,color:T.grey,width:8,textAlign:"right",marginRight:1}}>{row}</span>
              <div style={{display:"flex",gap:1.5}}>
                {Array.from({length:PREV_COLS},(_,i) => {
                  const id = `${row}${i+1}`;
                  const taken = TAKEN_SET.has(id);
                  const pairGap = i > 0 && i % 2 === 0;
                  return (
                    <div key={i} style={{display:"flex",marginLeft:pairGap?3:0}}>
                      {isAcc ? (
                        <div style={{width:11,height:10,borderRadius:"50%",background:taken?"rgba(100,160,255,.06)":"rgba(100,160,255,.15)",border:`1px solid ${taken?"rgba(100,160,255,.12)":"rgba(100,160,255,.35)"}`,display:"flex",alignItems:"center",justifyContent:"center",opacity:taken?.4:1}}>
                          <span style={{fontSize:5,color:"rgba(120,180,255,.7)"}}>♿</span>
                        </div>
                      ) : (
                        <div style={{
                          width:11,height:9,
                          borderRadius:"2px 2px 1px 1px",
                          background:taken?"rgba(255,255,255,.03)":isPrem?"rgba(139,92,246,.35)":T.navyLight,
                          border:`1px solid ${taken?"rgba(255,255,255,.04)":isPrem?"rgba(139,92,246,.4)":T.greyDim}`,
                          opacity:taken?.35:1,
                        }}/>
                      )}
                    </div>
                  );
                })}
              </div>
              <span style={{fontSize:6,color:T.grey,width:8,marginLeft:1}}>{row}</span>
            </div>
          );
          if (sec) {
            return [
              <div key={`sec-${row}`} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 8px 2px",width:"100%"}}>
                <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.glassBorder},transparent)`}}/>
                <span style={{fontSize:6,fontWeight:700,color:isPrem?T.goldLight:T.grey,letterSpacing:".1em"}}>{sec}</span>
                <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.glassBorder},transparent)`}}/>
              </div>,
              seatRow,
            ];
          }
          return [seatRow];
        })}
      </div>

      {/* Exit markers */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:4,marginTop:6,padding:"0 8px"}}>
        <span style={{fontSize:6,color:"rgba(39,174,96,.6)",fontWeight:700}}>EXIT</span>
        <div style={{flex:1,borderTop:`1px dashed ${T.greyDim}`}}/>
        <span style={{fontSize:6,color:T.greyDim,letterSpacing:".08em"}}>BACK</span>
        <div style={{flex:1,borderTop:`1px dashed ${T.greyDim}`}}/>
        <span style={{fontSize:6,color:"rgba(39,174,96,.6)",fontWeight:700}}>EXIT</span>
      </div>

      {/* Mini legend */}
      <div style={{display:"flex",gap:8,justifyContent:"center",marginTop:6}}>
        {[
          {bg:T.navyLight,bd:T.greyDim,l:"Available"},
          {bg:"rgba(139,92,246,.35)",bd:"rgba(139,92,246,.4)",l:"Center"},
          {bg:"rgba(255,255,255,.03)",bd:"rgba(255,255,255,.04)",l:"Taken"},
        ].map(({bg,bd,l})=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:2}}>
            <div style={{width:7,height:6,borderRadius:1,background:bg,border:`1px solid ${bd}`}}/>
            <span style={{fontSize:6,color:T.grey}}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── FILTER SHEET ─────────────────────────────────────────────────────────────
const FilterSheet = ({ onClose }) => {
  const [sortBy,setSortBy] = useState("Popularity");
  const [ratings,setRatings] = useState([]);
  const [genres,setGenres] = useState([]);
  const [runtime,setRuntime] = useState([]);
  const toggleArr = (arr,setArr,val) => setArr(a=>a.includes(val)?a.filter(x=>x!==val):[...a,val]);
  const GENRES = ["3D","Action/Adventure","Animated","Art House/Foreign","Comedy","Special Events","Documentary","Drama","Family","Horror","IMAX","LGBTQ+","Romance","Sci-Fi/Fantasy","Suspense/Thriller"];
  const RATINGS = ["G","PG","PG-13","R","NC-17","NC"];
  const RUNTIMES = ["Less than 1 hour","1 to 1.5 hours","1.5 to 2 hours","2 to 2.5 hours","More than 2.5 hours"];
  const total = ratings.length+genres.length+runtime.length;

  return (
    <div style={{position:"absolute",inset:0,zIndex:300,display:"flex",flexDirection:"column"}}>
      <div onClick={onClose} style={{flex:1,background:"rgba(0,0,0,.6)"}}/>
      <div style={{background:T.navyMid,borderRadius:"20px 20px 0 0",maxHeight:"85%",overflowY:"auto",padding:"0 0 40px"}}>
        {/* Handle */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${T.glassBorder}`,position:"sticky",top:0,background:T.navyMid,zIndex:10}}>
          <span style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>Filter & Sort</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.grey,fontSize:20,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{padding:"16px 20px"}}>
          {/* Sort */}
          <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:12,letterSpacing:".04em",textTransform:"uppercase"}}>Sort By</div>
          {["Popularity","Release Date (oldest to newest)","Release Date (newest to oldest)","Runtime"].map(s=>(
            <div key={s} onClick={()=>setSortBy(s)} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
              <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${sortBy===s?T.gold:T.greyDim}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {sortBy===s&&<div style={{width:9,height:9,borderRadius:"50%",background:T.gold}}/>}
              </div>
              <span style={{fontSize:14,color:sortBy===s?T.white:T.grey}}>{s}</span>
            </div>
          ))}

          {/* MPA Ratings */}
          <div style={{fontSize:13,fontWeight:700,color:T.white,margin:"20px 0 12px",letterSpacing:".04em",textTransform:"uppercase"}}>MPA Ratings</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {RATINGS.map(r=>(
              <button key={r} onClick={()=>toggleArr(ratings,setRatings,r)} style={{padding:"7px 14px",borderRadius:8,background:ratings.includes(r)?T.goldDim:"transparent",color:ratings.includes(r)?T.goldLight:T.offwhite,border:`1.5px solid ${ratings.includes(r)?T.goldBorder:T.greyDim}`,cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:600,fontSize:13}}>{r}</button>
            ))}
          </div>

          {/* Genres */}
          <div style={{fontSize:13,fontWeight:700,color:T.white,margin:"20px 0 12px",letterSpacing:".04em",textTransform:"uppercase"}}>Genres</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {GENRES.map(g=>(
              <button key={g} onClick={()=>toggleArr(genres,setGenres,g)} style={{padding:"7px 12px",borderRadius:8,background:genres.includes(g)?T.goldDim:"transparent",color:genres.includes(g)?T.goldLight:T.offwhite,border:`1.5px solid ${genres.includes(g)?T.goldBorder:T.greyDim}`,cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:500,fontSize:12}}>{g}</button>
            ))}
          </div>

          {/* Runtime */}
          <div style={{fontSize:13,fontWeight:700,color:T.white,margin:"20px 0 12px",letterSpacing:".04em",textTransform:"uppercase"}}>Runtime</div>
          {RUNTIMES.map(r=>(
            <div key={r} onClick={()=>toggleArr(runtime,setRuntime,r)} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 0",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
              <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${runtime.includes(r)?T.gold:T.greyDim}`,background:runtime.includes(r)?T.gold:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                {runtime.includes(r)&&<span style={{fontSize:11,color:T.navy,fontWeight:900}}>✓</span>}
              </div>
              <span style={{fontSize:14,color:runtime.includes(r)?T.white:T.grey}}>{r}</span>
            </div>
          ))}
        </div>
        {/* Footer */}
        <div style={{position:"sticky",bottom:0,background:T.navyMid,padding:"12px 20px",borderTop:`1px solid ${T.glassBorder}`,display:"flex",gap:12}}>
          <button onClick={()=>{setSortBy("Popularity");setRatings([]);setGenres([]);setRuntime([]);}} style={{background:"none",border:"none",color:T.gold,fontFamily:"'Outfit',sans-serif",fontWeight:600,fontSize:14,cursor:"pointer"}}>Clear All</button>
          <button className="btn-gold" onClick={onClose} style={{flex:1,padding:13,fontSize:14}}>Show {total>0?`(${total} filters) `:""}Movies</button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MOVIES HOME SCREEN
// ══════════════════════════════════════════════════════
const MoviesScreen = ({ nav, setMovie, favs, toggleFav }) => {
  const [tab, setTab] = useState("now");
  const [heroIdx, setHeroIdx] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const heroes = MOVIES.slice(0,5);
  const hero = heroes[heroIdx];

  const goMovie = (m) => { setMovie(m); nav("movie-detail"); };

  const PosterCard = ({ movie, width=150, height=215 }) => (
    <div className="press card-lift" onClick={()=>goMovie(movie)} style={{flexShrink:0,width,position:"relative"}}>
      <div style={{width,height,borderRadius:10,overflow:"hidden",position:"relative",border:`1px solid ${T.glassBorder}`}}>
        <MoviePoster movieId={movie.id} width={width} height={height}/>
        {/* Play overlay */}
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",opacity:0,transition:"opacity .2s"}} className="play-overlay">
          <div style={{width:36,height:36,borderRadius:"50%",border:`2px solid ${T.white}`,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:0,height:0,borderTop:"7px solid transparent",borderBottom:"7px solid transparent",borderLeft:`12px solid ${T.white}`,marginLeft:2}}/>
          </div>
        </div>
        {/* Heart */}
        <button onClick={e=>toggleFav(e,movie.id)} style={{position:"absolute",top:7,right:7,background:"rgba(0,0,0,.55)",border:"none",borderRadius:"50%",width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:favs.includes(movie.id)?T.gold:T.white,transition:"color .2s"}}>
          {favs.includes(movie.id)?"♥":"♡"}
        </button>
        {movie.releaseDate&&<div style={{position:"absolute",bottom:8,left:8,background:T.goldDim,border:`1px solid ${T.goldBorder}`,borderRadius:5,padding:"2px 7px",fontSize:10,fontWeight:700,color:T.goldLight}}>{movie.releaseDate}</div>}
      </div>
      <div style={{marginTop:7}}>
        <div style={{fontSize:12,fontWeight:600,color:T.white,lineHeight:1.3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{movie.title}</div>
        <div style={{fontSize:10,color:T.grey,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{movie.genre}</div>
        <div style={{display:"flex",alignItems:"center",gap:4,marginTop:2}}>
          <span style={{fontSize:10,color:T.grey,background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:3,padding:"0 4px",fontWeight:600}}>{movie.rating}</span>
          <span style={{fontSize:10,color:T.grey}}>{movie.runtime}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="screen" style={{paddingBottom:80,position:"relative",paddingTop:52}}>
      {showFilter && <FilterSheet onClose={()=>setShowFilter(false)}/>}

      {/* Header — fixed position */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px 10px",position:"fixed",top:0,left:0,right:0,zIndex:50,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`,maxWidth:360,margin:"0 auto"}}>
        <Logo size={0.9}/>
        <div style={{display:"flex",gap:10}}>
          <button style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:4}}><svg viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg></button>
          <button onClick={()=>nav("myreelview")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:"50%",width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><svg viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></button>
        </div>
      </div>

      {/* Hero Carousel — Fixed-height editorial marquee */}
      <div style={{margin:"14px 16px 0",position:"relative",height:230,cursor:"pointer"}} onClick={()=>goMovie(hero)}>
        {/* Poster — locked dimensions, left-aligned */}
        <div style={{position:"absolute",top:0,left:0,width:155,height:230}}>
          <div style={{position:"absolute",top:-3,left:-3,right:-3,bottom:-3,borderRadius:14,background:`linear-gradient(145deg,${T.gold},rgba(201,168,76,.3))`,opacity:.4}}/>
          <div style={{width:155,height:230,borderRadius:12,overflow:"hidden",position:"relative",border:`1.5px solid ${T.goldBorder}`,boxShadow:`0 16px 40px rgba(0,0,0,.6)`}}>
            <MoviePoster movieId={hero.id} width={155} height={230} mini/>
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.15)"}}>
              <div style={{width:38,height:38,borderRadius:"50%",border:`2px solid rgba(255,255,255,.85)`,background:"rgba(0,0,0,.35)",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}>
                <div style={{width:0,height:0,borderTop:"7px solid transparent",borderBottom:"7px solid transparent",borderLeft:`12px solid ${T.white}`,marginLeft:2}}/>
              </div>
            </div>
          </div>
        </div>
        {/* Info — top-right area, full titles visible */}
        <div style={{position:"absolute",top:0,left:172,right:0}}>
          <div style={{fontSize:9,fontWeight:700,color:T.gold,letterSpacing:".14em",marginBottom:8,textTransform:"uppercase"}}>
            ★ Featured This Week
          </div>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:21,fontWeight:900,color:T.white,lineHeight:1.15,marginBottom:8}}>{hero.title}</h2>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5,flexWrap:"wrap"}}>
            <span className="tag">{hero.rating}</span>
            <span className="tag">{hero.genre}</span>
          </div>
          <div style={{fontSize:11,color:T.grey}}>{hero.runtime} · {hero.year}</div>
        </div>
        {/* CTA — pinned bottom-right with breathing room */}
        <button className="btn-gold press" onClick={e=>{e.stopPropagation();goMovie(hero);}} style={{position:"absolute",bottom:0,right:0,padding:"10px 22px",fontSize:13,borderRadius:10,whiteSpace:"nowrap"}}>Get Tickets</button>
      </div>
      {/* Carousel dots */}
      <div style={{display:"flex",justifyContent:"center",gap:5,marginTop:10}}>
        {heroes.map((_,i)=>(
          <div key={i} onClick={()=>setHeroIdx(i)} style={{width:i===heroIdx?18:6,height:6,borderRadius:3,background:i===heroIdx?T.gold:T.greyDim,transition:"all .25s",cursor:"pointer"}}/>
        ))}
      </div>

      {/* Tabs + Filter */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px 0"}}>
        <div style={{display:"flex",gap:0,borderBottom:`1px solid ${T.glassBorder}`,flex:1}}>
          {[["now","Now Playing"],["soon","Coming Soon"],["fav","Favorites"]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} className={tab===id?"tab-on":"tab-off"} style={{background:"none",border:"none",cursor:"pointer",padding:"10px 14px 10px 0",fontSize:13,fontWeight:600,fontFamily:"'Outfit',sans-serif",marginRight:4,transition:"color .2s"}}>{label}</button>
          ))}
        </div>
        <button onClick={()=>setShowFilter(true)} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:8,padding:"7px 10px",cursor:"pointer",display:"flex",alignItems:"center",gap:5,marginBottom:1}}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 3h12M3 7h8M5 11h4" stroke={T.grey} strokeWidth="1.5" strokeLinecap="round"/></svg>
          <span style={{fontSize:11,color:T.grey,fontWeight:600}}>Filter</span>
        </button>
      </div>

      <div style={{padding:"16px 16px 0"}}>

        {/* Favorites */}
        {tab==="now" && (
          <div style={{marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <span className="section-label">Favorites</span>
                <span style={{fontSize:16}}>♥</span>
              </div>
              <button className="view-all" onClick={()=>setTab("fav")}>View all</button>
            </div>
            <div style={{fontSize:11,color:T.grey,marginBottom:12}}>Movies you've saved for later</div>
            {favs.length === 0 ? (
              <div style={{padding:"20px 14px",background:T.navyLight,border:`1px dashed ${T.greyDim}`,borderRadius:12,textAlign:"center"}}>
                <div style={{fontSize:22,marginBottom:6}}>♡</div>
                <div style={{fontSize:12,color:T.grey}}>No favorites yet — tap ♡ on any movie to save it here.</div>
              </div>
            ) : (
              <div className="hscroll">
                {MOVIES.filter(m=>favs.includes(m.id)).map(m=>(
                  <div key={m.id} className="press card-lift" onClick={()=>goMovie(m)} style={{flexShrink:0,width:148,borderRadius:12,overflow:"hidden",border:`1px solid ${T.goldBorder}`,position:"relative",background:T.navyCard}}>
                    <div style={{height:140,position:"relative",overflow:"hidden"}}>
                      {POSTER_IMAGES[m.id] && <img src={POSTER_IMAGES[m.id]} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}} loading="lazy"/>}
                      <div style={{position:"absolute",inset:0,background:`linear-gradient(180deg,transparent 60%,rgba(0,0,0,.4) 100%)`}}/>
                      <button onClick={e=>toggleFav(e,m.id)} aria-label={`Remove ${m.title} from favorites`} style={{position:"absolute",top:7,right:7,background:"rgba(0,0,0,.6)",backdropFilter:"blur(4px)",border:`1px solid ${T.goldBorder}`,borderRadius:"50%",width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:T.gold,zIndex:2,transition:"all .18s",padding:0}}>♥</button>
                    </div>
                    <div style={{padding:"9px 10px 11px"}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginBottom:3}}>{m.title}</div>
                      <div style={{fontSize:10,color:T.grey}}>{m.genre} · {m.runtime}</div>
                      <button className="btn-gold" style={{width:"100%",padding:"6px 0",fontSize:10,marginTop:8,borderRadius:7}} onClick={e=>{e.stopPropagation();goMovie(m);}}>Get Tickets</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Now Playing grid */}
        {tab==="now" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <span className="section-label">Now Playing</span>
              <div style={{display:"flex",alignItems:"center",gap:5}}>
                <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 1C3.24 1 1 3.24 1 6s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z" stroke={T.grey} strokeWidth="1.2" fill="none"/><path d="M6 3v4l2.5 1.5" stroke={T.grey} strokeWidth="1.2" strokeLinecap="round" fill="none"/></svg>
                <span style={{fontSize:11,color:T.grey}}>Los Angeles, CA</span>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
              {MOVIES.map(m=><PosterCard key={m.id} movie={m}/>)}
            </div>
            <button className="btn-outline" disabled style={{width:"100%",padding:13,fontSize:14,marginBottom:8,cursor:"default",borderColor:"rgba(201,168,76,0.5)",color:T.gold}} aria-label="No more movies to show">View More</button>
          </div>
        )}

        {tab==="soon" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <span className="section-label">Coming Soon</span>
              <button className="view-all">View all</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
              {COMING_SOON.map(m=><PosterCard key={m.id} movie={m}/>)}
            </div>
          </div>
        )}

        {tab==="fav" && (
          <div>
            <div style={{fontSize:13,color:T.grey,marginBottom:14}}>Your favorited movies</div>
            {favs.length===0 && (
              <div style={{textAlign:"center",padding:"40px 0"}}>
                <div style={{fontSize:32,marginBottom:10}}>♡</div>
                <div style={{color:T.grey,fontSize:14,marginBottom:4}}>No favorites yet</div>
                <div style={{color:T.grey,fontSize:12}}>Tap ♡ on any movie to add it here.</div>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              {[...MOVIES,...COMING_SOON].filter(m=>favs.includes(m.id)).map(m=>(
                <div key={m.id} style={{position:"relative"}}>
                  <PosterCard movie={m}/>
                  <button onClick={e=>toggleFav(e,m.id)} style={{position:"absolute",top:7,right:7,background:"rgba(0,0,0,.7)",border:`1.5px solid ${T.goldBorder}`,borderRadius:"50%",width:30,height:30,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:T.gold,zIndex:5,transition:"all .18s",backdropFilter:"blur(4px)"}} title="Remove from Favorites">♥</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MOVIE DETAIL SCREEN — ReelView visual identity
// ══════════════════════════════════════════════════════
const MovieDetailScreen = ({ movie, nav, setShowtime, setTheater, setTheaterInitTab, favs, toggleFav }) => {
  const [tab, setTab] = useState("showtimes");
  const [openPreview, setOpenPreview] = useState(null);
  const [selectedDate, setSelectedDate] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const [fmtFilter, setFmtFilter] = useState("All Formats");
  if (!movie) return null;
  const isComingSoon = !!movie.releaseDate;
  const dates = ["Today","Tue 6/3","Wed 6/4","Thu 6/5","Fri 6/6","Sat 6/7","Sun 6/8"];

  const handleTime = (theater, fmt, time) => {
    const base = FORMAT_PRICING[fmt.fmt] || 16.50;
    const mat = isMatinee(time);
    const price = mat ? base - MATINEE_DISCOUNT : base;
    setShowtime({ movie, theater, fmt: fmt.fmt, time, date: dates[selectedDate], basePrice: price, isMatinee: mat, showroom: fmt.showroom, seatsLeft: fmt.seatsLeft });
    nav("seat-map");
  };

  const TABS = ["Showtimes","Details","Videos"];

  return (
    <div className="screen" style={{paddingBottom:80}}>

      {/* ── HERO: full-bleed poster atmosphere with ReelView gold treatment ── */}
      <div style={{position:"relative",height:300,overflow:"hidden"}}>
        {/* Atmospheric backdrop */}
        <div style={{position:"absolute",inset:0,background:`linear-gradient(145deg,${movie.color} 0%,#04081A 75%)`}}/>
        {/* Subtle grain texture overlay */}
        <div style={{position:"absolute",inset:0,backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",opacity:.4}}/>
        {/* Large poster art, offset right */}
        <div style={{position:"absolute",right:-10,top:"50%",transform:"translateY(-54%)",opacity:.18,filter:"blur(2px)",userSelect:"none"}}><MoviePoster movieId={movie.id} width={160} height={220} mini/></div>
        {/* Crisp centered poster */}
        <div style={{position:"absolute",left:20,top:"50%",transform:"translateY(-50%)",width:110,height:160,borderRadius:12,overflow:"hidden",border:`1px solid ${T.goldBorder}`,boxShadow:`0 20px 50px rgba(0,0,0,.65), 0 0 0 1px ${T.goldBorder}`}}>
          <MoviePoster movieId={movie.id} width={110} height={160} mini/>
        </div>
        {/* Title + meta stacked right of poster */}
        <div style={{position:"absolute",left:146,right:52,top:"50%",transform:"translateY(-50%)"}}>
          <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
            <span className="tag">{movie.rating}</span>
            <span className="tag">{movie.genre}</span>
          </div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:18,lineHeight:1.2,color:T.white,marginBottom:8}}>{movie.title}</h1>
          <div style={{fontSize:11,color:T.grey}}>{movie.runtime}</div>
          <div style={{fontSize:10,color:T.grey,opacity:.7,marginTop:2}}>{movie.year}</div>
        </div>
        {/* Back btn */}
        <button onClick={()=>nav("movies")} style={{position:"absolute",top:14,left:14,background:"rgba(4,8,26,.7)",backdropFilter:"blur(10px)",border:`1px solid ${T.glassBorder}`,borderRadius:10,width:34,height:34,cursor:"pointer",color:T.white,fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",zIndex:10}}>←</button>
        {/* Share btn */}
        <button style={{position:"absolute",top:14,right:14,background:"rgba(4,8,26,.7)",backdropFilter:"blur(10px)",border:`1px solid ${T.glassBorder}`,borderRadius:10,width:34,height:34,cursor:"pointer",color:T.gold,display:"flex",alignItems:"center",justifyContent:"center",zIndex:10}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
        </button>
        {/* Bottom fade */}
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:80,background:"linear-gradient(to top,#080D1A,transparent)"}}/>
      </div>

      {/* ── TAB SWITCHER — pill style, ReelView branded ── */}
      <div style={{padding:"14px 16px 0",background:T.navy}}>
        <div style={{display:"flex",background:T.navyLight,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:3,gap:2}}>
          {TABS.map(t=>{
            const key=t.toLowerCase();
            const on=tab===key;
            return (
              <button key={t} onClick={()=>setTab(key)} style={{flex:1,padding:"8px 0",borderRadius:9,background:on?T.gold:"transparent",color:on?T.navy:T.grey,border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:700,fontSize:12,letterSpacing:".03em",transition:"all .2s"}}>
                {t}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── SHOWTIMES TAB ── */}
      {tab==="showtimes" && isComingSoon && (
        <div className="fade" style={{padding:"24px 16px 100px",textAlign:"center"}}>
          <div style={{background:`linear-gradient(145deg,rgba(201,168,76,.06),rgba(201,168,76,.02))`,border:`1px solid ${T.goldBorder}`,borderRadius:18,padding:"32px 20px"}}>
            <div style={{fontSize:36,marginBottom:12}}>🎬</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:T.white,marginBottom:8}}>Coming {movie.releaseDate}</div>
            <p style={{fontSize:13,color:T.offwhite,lineHeight:1.6,marginBottom:20,maxWidth:280,margin:"0 auto 20px"}}>
              Showtimes aren't available yet. Check back closer to release day, or favorite this movie to stay in the loop!
            </p>
            {favs && toggleFav && (
              <button className={favs.includes(movie.id)?"btn-outline":"btn-gold"} onClick={e=>toggleFav(e,movie.id)} style={{padding:"11px 24px",fontSize:13,borderRadius:10,display:"inline-flex",alignItems:"center",gap:7}}>
                <span style={{fontSize:15}}>{favs.includes(movie.id)?"♥":"♡"}</span>
                {favs.includes(movie.id)?"Saved to Favorites":"Add to Favorites"}
              </button>
            )}
          </div>
        </div>
      )}
      {tab==="showtimes" && !isComingSoon && (
        <div className="fade" style={{padding:"16px 16px"}}>

          {/* Date scroller */}
          <div className="hscroll" style={{marginBottom:16,gap:7}}>
            {dates.map((d,i)=>(
              <button key={d} onClick={()=>setSelectedDate(i)} style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",padding:"9px 13px",borderRadius:12,background:selectedDate===i?T.goldDim:"transparent",color:selectedDate===i?T.goldLight:T.grey,border:`1.5px solid ${selectedDate===i?T.goldBorder:T.greyDim}`,cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all .18s"}}>
                <span style={{fontSize:9,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",marginBottom:2}}>{i===0?"TODAY":d.split(" ")[0].toUpperCase()}</span>
                <span style={{fontSize:14,fontWeight:700}}>{i===0?"Now":d.split(" ")[1]}</span>
              </button>
            ))}
          </div>

          {/* Quick-filter chips: format */}
          <div className="hscroll" style={{marginBottom:18,gap:7}}>
            {["All Formats","IMAX","Dolby Atmos","Standard"].map(f=>{
              const on = fmtFilter===f;
              return (
                <button key={f} onClick={()=>setFmtFilter(f)} style={{flexShrink:0,padding:"6px 13px",borderRadius:20,background:on?T.goldDim:"transparent",border:`1.5px solid ${on?T.goldBorder:T.greyDim}`,color:on?T.goldLight:T.grey,fontFamily:"'Outfit',sans-serif",fontWeight:600,fontSize:11,cursor:"pointer",whiteSpace:"nowrap",transition:"all .18s"}}>{f}</button>
              );
            })}
          </div>

          {/* Theaters */}
          {THEATERS.map(th=>{
            const thData = SHOWTIMES_DATA[th.id] || { formats:[{ fmt:"Standard", tagline:"", amenities:"Heated Recliners · Reserved Seating", times:["2:00 PM","5:00 PM","8:00 PM"] }] };
            const filteredFormats = fmtFilter==="All Formats" ? thData.formats : thData.formats.filter(f=>f.fmt===fmtFilter);
            if(filteredFormats.length===0) return null;
            return (
              <div key={th.id} className="glass-card" style={{marginBottom:14,overflow:"hidden"}}>
                {/* Theater header */}
                <div onClick={()=>{setTheater(th);setTheaterInitTab("info");nav("theater-detail");}} style={{padding:"14px 16px 10px",position:"relative",cursor:"pointer"}} role="button" tabIndex={0} aria-label={`View details for ${th.name}`}>
                  <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${T.gold},rgba(201,168,76,.2))`}} role="presentation"/>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:T.white,lineHeight:1.25,marginBottom:4}}>{th.name}</div>
                      <div style={{fontSize:11,color:T.gold}}>{th.distance} · {th.address}</div>
                    </div>
                    <span style={{color:T.grey,fontSize:14}}>›</span>
                  </div>
                </div>
                {/* Format sections */}
                {filteredFormats.map((fmt,fi)=>{
                  const previewKey=`${th.id}-${fi}`;
                  return (
                    <div key={fi} style={{borderTop:`1px solid ${T.glassBorder}`,padding:"12px 16px"}}>
                      {/* Format badge + from-price */}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{background:`linear-gradient(135deg,${T.goldDim},rgba(201,168,76,.05))`,border:`1px solid ${T.goldBorder}`,borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:800,color:T.goldLight,letterSpacing:".04em"}}>{fmt.fmt}</span>
                          {fmt.tagline&&<span style={{fontSize:9,color:T.grey,fontStyle:"italic"}}>{fmt.tagline}</span>}
                        </div>
                        <span style={{fontSize:9,color:T.offwhite,fontWeight:600}}>from <span style={{color:T.white,fontWeight:700}}>${((FORMAT_PRICING[fmt.fmt]||16.50) - MATINEE_DISCOUNT + SERVICE_FEE_GLOBAL).toFixed(2)}</span></span>
                      </div>
                      <div style={{fontSize:9,color:T.grey,marginBottom:3,lineHeight:1.4}}>{fmt.amenities}</div>
                      {fmt.showroom&&SHOWROOMS[fmt.showroom]&&(
                        <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:8}}>
                          <span style={{fontSize:8,color:T.offwhite,fontWeight:600}}>{SHOWROOMS[fmt.showroom].name}</span>
                          <span style={{fontSize:7,color:T.grey}}>· {SHOWROOMS[fmt.showroom].seats} seats · {SHOWROOMS[fmt.showroom].tag}</span>
                        </div>
                      )}
                      {/* Time pills with all-in pricing */}
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                        {fmt.times.map(t=>{
                          const base = FORMAT_PRICING[fmt.fmt] || 16.50;
                          const mat = isMatinee(t);
                          const allIn = (mat ? base - MATINEE_DISCOUNT : base) + SERVICE_FEE_GLOBAL;
                          const left = fmt.seatsLeft?.[t];
                          const lowSeats = left && left <= 20;
                          return (
                            <button key={t} className="pill-time press" onClick={()=>handleTime(th,fmt,t)}
                              style={{fontSize:11,padding:"6px 10px",display:"flex",flexDirection:"column",alignItems:"center",gap:2,position:"relative"}}
                              aria-label={`${t} showtime, $${allIn.toFixed(2)}${mat?" matinee pricing":""}${lowSeats?`, only ${left} seats left`:""}`}>
                              <span>{t}</span>
                              <span style={{fontSize:8,color:mat?T.green:T.grey,fontWeight:600}}>
                                ${allIn.toFixed(2)}{mat&&" ✦"}
                              </span>
                              {mat && <span style={{position:"absolute",top:-6,right:-4,background:T.green,color:"#fff",fontSize:6,fontWeight:800,borderRadius:3,padding:"1px 3px",letterSpacing:".03em"}}>MATINEE</span>}
                              {lowSeats && <span style={{fontSize:7,color:T.red,fontWeight:700}}>{left<=10?`${left} left!`:`${left} left`}</span>}
                            </button>
                          );
                        })}
                      </div>
                      {/* Fee transparency */}
                      <div style={{fontSize:8,color:T.grey,display:"flex",alignItems:"center",gap:3,marginBottom:8}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                        Prices include ${SERVICE_FEE_GLOBAL.toFixed(2)} fee · No hidden charges
                      </div>
                      {/* Check Seats */}
                      <button onClick={()=>setOpenPreview(openPreview===previewKey?null:previewKey)} aria-expanded={openPreview===previewKey} aria-label="Toggle seat availability preview" style={{display:"inline-flex",alignItems:"center",gap:6,background:openPreview===previewKey?T.goldDim:"rgba(255,255,255,.03)",border:`1px solid ${openPreview===previewKey?T.goldBorder:T.greyDim}`,borderRadius:8,padding:"6px 12px",cursor:"pointer",transition:"all .18s"}}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={openPreview===previewKey?T.goldLight:T.grey} strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        <span style={{fontSize:11,color:openPreview===previewKey?T.goldLight:T.grey,fontWeight:600,fontFamily:"'Outfit',sans-serif"}}>Check Seats</span>
                        <span style={{fontSize:9,color:T.grey,transform:openPreview===previewKey?"rotate(180deg)":"none",display:"inline-block",transition:"transform .2s"}}>▾</span>
                      </button>
                      {openPreview===previewKey&&<InlineSeatPreview showtimeKey={`${th.id}-${movie.id}-${fmt.fmt}`} times={fmt.times} seatsLeft={fmt.seatsLeft}/>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* ── DETAILS TAB ── */}
      {tab==="details" && (
        <div className="fade" style={{padding:"18px 16px"}}>

          {/* Critical Reception — horizontal strip instead of AMC grid cards */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Critical Reception</div>
            <div style={{background:`linear-gradient(135deg,${T.navyCard},${T.navyLight})`,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:"14px 16px",display:"flex",alignItems:"center",gap:0}}>
              {/* Tomatometer */}
              <div style={{flex:1,display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:42,height:42,borderRadius:10,background:movie.rtFresh>=60?"rgba(201,168,76,.12)":"rgba(224,75,61,.12)",border:`1.5px solid ${movie.rtFresh>=60?T.goldBorder:"rgba(224,75,61,.25)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>🍅</div>
                <div>
                  <div style={{fontSize:16,fontWeight:800,color:movie.rtFresh>=60?T.goldLight:T.red}}>{movie.rtFresh}%</div>
                  <div style={{fontSize:9,color:T.grey}}>Tomatometer</div>
                </div>
              </div>
              {/* Divider */}
              <div style={{width:1,height:36,background:T.glassBorder,margin:"0 12px"}}/>
              {/* Audience */}
              <div style={{flex:1,display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:42,height:42,borderRadius:10,background:movie.rtAudience>=60?"rgba(147,112,219,.12)":"rgba(224,75,61,.12)",border:`1.5px solid ${movie.rtAudience>=60?"rgba(147,112,219,.25)":"rgba(224,75,61,.25)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>🍿</div>
                <div>
                  <div style={{fontSize:16,fontWeight:800,color:movie.rtAudience>=60?"rgba(147,112,219,.9)":T.red}}>{movie.rtAudience}%</div>
                  <div style={{fontSize:9,color:T.grey}}>Audience Score</div>
                </div>
              </div>
            </div>
            {/* User score */}
            {movie.score && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:8}}>
                <span style={{fontSize:10,color:T.grey}}>IMDb</span>
                <span style={{fontSize:13,fontWeight:800,color:T.goldLight}}>{movie.score}</span>
                <span style={{fontSize:10,color:T.grey}}>/ 10</span>
              </div>
            )}
          </div>

          {/* Synopsis — pull-quote editorial style */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Synopsis</div>
            <div style={{borderLeft:`2px solid ${T.gold}`,paddingLeft:14}}>
              <p style={{fontSize:14,color:T.offwhite,lineHeight:1.75,fontStyle:"italic",marginBottom:6}}>{descExpanded?movie.desc:movie.desc.slice(0,130)+"..."}</p>
              <button onClick={()=>setDescExpanded(!descExpanded)} style={{background:"none",border:"none",cursor:"pointer",color:T.gold,fontSize:12,fontWeight:700,fontFamily:"'Outfit',sans-serif",padding:0,display:"flex",alignItems:"center",gap:4}}>
                {descExpanded?"Show less ▴":"Read more ▾"}
              </button>
            </div>
          </div>

          {/* Cast & Crew — compact list cards instead of circular avatars */}
          <div style={{marginBottom:22}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Cast & Crew</div>
            {/* Director featured */}
            <div style={{background:`linear-gradient(135deg,rgba(201,168,76,.08),rgba(201,168,76,.02))`,border:`1px solid ${T.goldBorder}`,borderRadius:12,padding:"10px 14px",marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:40,height:40,borderRadius:10,background:`linear-gradient(135deg,${T.navyLight},${T.greyDim})`,border:`1.5px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:T.gold}}>{movie.director[0]}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:T.white}}>{movie.director}</div>
                <div style={{fontSize:10,color:T.gold,fontWeight:600}}>Director</div>
              </div>
            </div>
            {/* Cast in compact 2-col grid */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              {movie.cast.map(name=>(
                <div key={name} style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:10,padding:"8px 10px",display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:30,height:30,borderRadius:8,background:`linear-gradient(135deg,${T.navyLight},${T.greyDim})`,border:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:600,color:T.offwhite,flexShrink:0}}>{name[0]}</div>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:600,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{name.split(" ")[0]}</div>
                    <div style={{fontSize:8,color:T.grey}}>{name.split(" ").slice(1).join(" ")}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Movie Stills */}
          <div>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Stills</div>
            <div className="hscroll" style={{gap:10}}>
              {[0,1,2].map(i=>(
                <div key={i} style={{flexShrink:0,width:168,height:95,borderRadius:10,overflow:"hidden",border:`1px solid ${T.glassBorder}`,opacity:.8+i*.07,position:"relative"}}><MoviePoster movieId={movie.id} width={168} height={95} mini/><div style={{position:"absolute",inset:0,background:`linear-gradient(${90+i*40}deg,transparent 40%,rgba(0,0,0,.3))`}}/></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── VIDEOS TAB ── */}
      {tab==="videos" && (
        <div className="fade" style={{padding:"16px"}}>
          {[{title:`${movie.title} — Official Trailer`,age:"1 month ago",dur:"2:18",label:"TRAILER"},{title:`${movie.title} — Behind the Camera`,age:"3 weeks ago",dur:"4:05",label:"FEATURETTE"},{title:`${movie.title} — Director's Cut Commentary`,age:"2 weeks ago",dur:"6:41",label:"EXCLUSIVE"}].map((v,i)=>(
            <div key={i} className="glass-card card-lift" style={{marginBottom:12,overflow:"hidden",cursor:"pointer"}}>
              {/* Thumbnail */}
              <div style={{position:"relative",height:160,background:`linear-gradient(${145+i*18}deg,${movie.color} 0%,#060C1C 100%)`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {POSTER_IMAGES[movie.id] && <img src={POSTER_IMAGES[movie.id]} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center 30%",opacity:.45}} loading="lazy"/>}
                {/* Vignette */}
                <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse at center,transparent 30%,rgba(0,0,0,.55))"}}/>
                {/* Gold-ring play button */}
                <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:48,height:48,borderRadius:"50%",border:`2px solid ${T.gold}`,background:"rgba(4,8,26,.55)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 20px ${T.goldDim}`}}>
                  <div style={{width:0,height:0,borderTop:"9px solid transparent",borderBottom:"9px solid transparent",borderLeft:`15px solid ${T.gold}`,marginLeft:2}}/>
                </div>
                {/* Duration badge */}
                <div style={{position:"absolute",bottom:8,right:10,background:"rgba(4,8,26,.8)",border:`1px solid ${T.glassBorder}`,borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:700,color:T.white}}>{v.dur}</div>
                {/* Label badge */}
                <span className="tag" style={{position:"absolute",top:8,left:10}}>{v.label}</span>
              </div>
              {/* Meta */}
              <div style={{padding:"10px 14px 12px",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:T.white,marginBottom:3,lineHeight:1.3}}>{v.title}</div>
                  <div style={{fontSize:10,color:T.grey}}>{v.age}</div>
                </div>
                <button style={{background:"none",border:"none",cursor:"pointer",color:T.grey,padding:"2px 4px",flexShrink:0}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════
// SEAT MAP
// ══════════════════════════════════════════════════════
const SeatMapScreen = ({ showtime, nav, setSeatData, seatTimer, setSeatTimer }) => {
  const ROWS = ["A","B","C","D","E","F","G","H","I","J","K"];
  const COLS = 12;
  const ACCESSIBLE_ROW = "D";
  // Deterministic PRNG — same algorithm as InlineSeatPreview so "Check Seats" matches
  const hashStr = s => { let h=0; for(let i=0;i<s.length;i++) h=((h<<5)-h)+s.charCodeAt(i)|0; return Math.abs(h); };
  const showtimeKey = `${showtime?.theater?.id}-${showtime?.movie?.id}-${showtime?.fmt}`;
  const seed = hashStr(`${showtimeKey}-${showtime?.time}`);
  const prng = i => { let h=(seed+i*374761393)|0; h=Math.imul(h^(h>>>15),0x1a85ec53); h=Math.imul(h^(h>>>13),0xc4ae3d47); return ((h^(h>>>16))>>>0)/4294967296; };
  const allSeats = ROWS.flatMap(r=>Array.from({length:COLS},(_,i)=>`${r}${i+1}`));
  const left = showtime?.seatsLeft?.[showtime?.time];
  const fillPct = left!=null ? Math.min(0.82, Math.max(0.25, 1-left/(ROWS.length*COLS))) : 0.12+prng(999)*0.28;
  const TAKEN = new Set();
  allSeats.forEach((s,i) => { if(prng(i+1)<fillPct) TAKEN.add(s); });
  const CENTER_ROWS = ["E","F","G"];
  const CENTER = CENTER_ROWS.flatMap(r => Array.from({length:COLS},(_,i)=>`${r}${i+1}`));
  const [selected, setSelected] = useState([]);
  const [povSeat, setPovSeat] = useState(null);
  const [expandedPOV, setExpandedPOV] = useState(null);

  // Start timer on first mount if not already running
  useEffect(() => { if (seatTimer === null) setSeatTimer(600); }, []);
  const timeLeft = seatTimer || 0;
  const timerMins = Math.floor(timeLeft / 60);
  const timerSecs = timeLeft % 60;
  const timerStr = `${timerMins}:${String(timerSecs).padStart(2,'0')}`;
  const timerUrgent = timeLeft < 120;

  const isPairGap = col => col > 0 && col % 2 === 0 && col < COLS; // small gap between every pair
  const rowZone = r => r <= "C" ? "Front" : r <= "G" ? "Middle" : "Back";
  const sectionFor = r => {
    if (r === "A") return { label: "FRONT", sub: "" };
    if (r === "E") return { label: "CENTER", sub: "" };
    if (r === "H") return { label: "REAR", sub: "Elevated · Rows H–K" };
    return null;
  };

  const toggle = id => {
    if(TAKEN.has(id)) return;
    setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : s.length < 8 ? [...s,id] : s);
    setPovSeat(id);
  };

  if(!showtime) return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:12,borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
        <button onClick={()=>nav("movie-detail")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:36,height:36,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:16}}>←</button>
        <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16}}>Select Seats</span>
      </div>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:T.grey,fontSize:13}}>Please select a showtime first.</div>
    </div>
  );

  const seatPrice = showtime?.basePrice || FORMAT_PRICING[showtime?.fmt] || 16.50;
  const seatFee = SERVICE_FEE_GLOBAL * selected.length;
  const totalEst = (selected.length * seatPrice) + seatFee;

  // Mini POV view inside each per-seat card
  const distFor = z => z==="Front"?"~15 ft":z==="Middle"?"~35 ft":"~55 ft";
  const angleFor = z => z==="Front"?"Front rows":z==="Middle"?"Center rows":"Elevated";
  const MiniPOV = ({ seatId, isActive, large }) => {
    const zone = rowZone(seatId[0]);
    const seatCol = parseInt(seatId.slice(1));
    const distPct = zone==="Front"?90:zone==="Middle"?65:42;
    const dist = distFor(zone);
    const h = large?200:74;
    const seatH = large?50:26;
    const backH = large?10:5;
    const cushH = large?28:15;
    const hiBackH = large?14:7;
    const hiCushH = large?34:18;
    const seatGap = large?2:1;
    const aisleW = large?8:4;
    // Paired layout matching the actual seat map — 12 seats in pairs of 2
    return (
      <div style={{position:"relative",height:h,background:"linear-gradient(180deg,#05101d 0%,#0c1929 55%,#111f30 100%)",overflow:"hidden"}}>
        {[12,30,50,70,88].map((x,i)=>(
          <div key={i} style={{position:"absolute",top:0,left:`${x}%`,width:1,height:large?30:16,background:`linear-gradient(to bottom,rgba(201,168,76,.45),transparent)`}}/>
        ))}
        <div style={{position:"absolute",top:large?14:7,left:"50%",transform:"translateX(-50%)",width:`${distPct}%`,height:large?22:11,background:"linear-gradient(90deg,transparent 3%,rgba(210,225,255,.14) 20%,rgba(210,225,255,.18) 80%,transparent 97%)",borderRadius:2}}/>
        <div style={{position:"absolute",top:large?44:22,left:"50%",transform:"translateX(-50%)",fontSize:large?10:6,color:"rgba(190,205,230,.3)",letterSpacing:".14em"}}>SCREEN</div>
        {[75,64,55,47].map((y,i)=>(
          <div key={i} style={{position:"absolute",top:`${y}%`,left:`${8+i*2}%`,right:`${8+i*2}%`,height:large?2.5:1.5,background:`rgba(201,168,76,${0.03+i*.025})`,borderRadius:1}}/>
        ))}
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:seatH,display:"flex",gap:0,padding:`0 ${large?16:6}px`,alignItems:"flex-end",justifyContent:"center"}}>
          {(() => {
            // Show only ~5 seats: the selected seat + 2 neighbors on each side
            const totalCols = 12;
            const start = Math.max(1, Math.min(seatCol - 2, totalCols - 4));
            const end = Math.min(totalCols, start + 4);
            const seats = [];
            for (let col = start; col <= end; col++) {
              const isYou = col === seatCol;
              const clr = isYou ? "rgba(201,168,76,.5)" : "rgba(35,50,72,.9)";
              const bdr = `1px solid rgba(201,168,76,${isYou ? .5 : .1})`;
              const bH = isYou ? hiBackH : backH;
              const cH = isYou ? hiCushH : cushH;
              const rd = large ? 3 : 2;
              const idx = col - start;
              const pairGap = idx > 0 && col % 2 === 1 ? (large ? 5 : 3) : 0;
              const w = large ? 40 : 18;
              seats.push(
                <div key={col} style={{width:w,display:"flex",flexDirection:"column",alignItems:"center",marginLeft:pairGap}}>
                  <div style={{width:"72%",height:bH,borderRadius:`${rd}px ${rd}px 0 0`,background:clr,border:bdr,borderBottom:"none"}}/>
                  <div style={{width:"100%",height:cH,borderRadius:`1px 1px ${rd}px ${rd}px`,background:`linear-gradient(180deg,${clr} 0%,rgba(18,28,46,.95) 100%)`,border:bdr,borderTop:"none"}}/>
                </div>
              );
            }
            return seats;
          })()}
        </div>
        <div style={{position:"absolute",top:large?8:4,left:large?10:5,background:"rgba(4,8,26,.85)",border:`1px solid ${T.glassBorder}`,borderRadius:large?6:4,padding:large?"4px 8px":"2px 5px",fontSize:large?11:7,color:T.goldLight,fontWeight:700,display:"flex",alignItems:"center",gap:large?5:3}}>
          👁 {zone[0]}
          <span style={{fontSize:large?9:6,color:T.grey,fontWeight:500}}>· {dist}</span>
        </div>
      </div>
    );
  };

  return (
    // Flex column fills the phone height — header top, scrollable middle, fixed bottom
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>

      {/* ── Header (sticky at top of flex column) ── */}
      <div style={{flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <div style={{padding:"11px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
          <button onClick={()=>nav("movie-detail")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0,marginTop:1}}>←</button>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:15,color:T.white,lineHeight:1.2}}>{showtime.movie?.title}</div>
            <div style={{fontSize:10,color:T.grey,marginTop:2}}>{showtime.theater?.name} · {showtime.date} · {showtime.time} · {showtime.fmt}</div>
            <div style={{display:"flex",gap:5,marginTop:4,flexWrap:"wrap"}}>
              <span style={{background:T.goldDim,border:`1px solid ${T.goldBorder}`,borderRadius:4,padding:"1px 6px",fontSize:8,fontWeight:700,color:T.goldLight,letterSpacing:".06em"}}>IMAX</span>
              <span style={{background:"rgba(255,255,255,.04)",border:`1px solid ${T.glassBorder}`,borderRadius:4,padding:"1px 6px",fontSize:8,color:T.offwhite,letterSpacing:".04em"}}>{(SHOWROOMS[showtime?.showroom]?.name||"Auditorium").toUpperCase()} · {ROWS.length} ROWS</span>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3,flexShrink:0}}>
            <div style={{
              background:timerUrgent?"rgba(192,57,43,.22)":"rgba(192,57,43,.12)",
              border:`1px solid ${timerUrgent?"rgba(231,76,60,.5)":"rgba(192,57,43,.25)"}`,
              borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,
              color:timerUrgent?"#ff6b6b":"#e74c3c",
              display:"flex",alignItems:"center",gap:3,
              animation:timerUrgent?"pulse 1s ease-in-out infinite":"none",
              transition:"all .3s ease"
            }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              {timerStr}
            </div>
            <button style={{background:"none",border:"none",cursor:"pointer",color:T.grey,fontSize:10,display:"flex",alignItems:"center",gap:3,padding:0}}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
              Share
            </button>
          </div>
        </div>
        {/* Theater capacity indicator */}
        <div style={{padding:"0 16px 8px",display:"flex",alignItems:"center",gap:6}}>
          <div style={{flex:1,height:3,borderRadius:2,background:T.greyDim,overflow:"hidden"}}>
            <div style={{width:`${Math.round((TAKEN.size / (ROWS.length * COLS)) * 100)}%`,height:"100%",borderRadius:2,background:`linear-gradient(90deg,${T.green},rgba(39,174,96,.6))`}}/>
          </div>
          <span style={{fontSize:9,color:T.grey,flexShrink:0}}>{Math.round(100 - (TAKEN.size / (ROWS.length * COLS)) * 100)}% available</span>
        </div>
        {/* Rewards status — member already */}
        <div style={{background:`linear-gradient(135deg,rgba(201,168,76,.08),rgba(201,168,76,.02))`,borderTop:`1px solid ${T.goldBorder}`,padding:"7px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:10,color:T.offwhite}}>⭐ You'll earn <span style={{color:T.goldLight,fontWeight:700}}>250 pts</span> on this purchase</div>
          <span style={{fontSize:9,color:T.goldLight,fontWeight:700}}>5,240 pts</span>
        </div>
      </div>

      {/* ── Scrollable seat grid + legend ── */}
      <div style={{flex:1,overflowY:"auto"}}>
        {/* Screen arc */}
        <div style={{padding:"16px 10px 4px",textAlign:"center"}}>
          <svg width="290" height="26" viewBox="0 0 290 26" fill="none" style={{display:"block",margin:"0 auto"}}>
            <path d="M6 24 Q145 2 284 24" stroke={T.gold} strokeWidth="2.5" fill="none" opacity=".7" strokeLinecap="round"/>
            <path d="M6 24 Q145 2 284 24" stroke={T.goldLight} strokeWidth="1" fill="none" opacity=".3" strokeLinecap="round"/>
          </svg>
          <div style={{fontSize:9,color:T.grey,letterSpacing:".2em",marginTop:2}}>S C R E E N</div>
        </div>

        {/* Seat grid with section labels */}
        <div style={{padding:"6px 6px 10px",overflowX:"auto"}}>
          <div style={{display:"flex",flexDirection:"column",gap:0,alignItems:"center",minWidth:290}}>
            {ROWS.flatMap(row => {
              const section = sectionFor(row);
              const seatRow = (
                <div key={row} style={{display:"flex",alignItems:"center",gap:2,marginBottom:4}}>
                  <span style={{fontSize:9,color:T.grey,width:10,textAlign:"right",flexShrink:0,marginRight:2}}>{row}</span>
                  {row===ACCESSIBLE_ROW ? (
                    <div style={{display:"flex",gap:0,alignItems:"center"}}>
                      {Array.from({length:COLS},(_,i)=>{
                        const id=`${row}${i+1}`;
                        const isTaken=TAKEN.has(id);
                        const isSel=selected.includes(id);
                        return (
                          <div key={i} style={{display:"flex",gap:0,marginLeft:isPairGap(i)?4:0}}>
                            <div onClick={()=>toggle(id)} style={{width:20,height:20,borderRadius:"50%",background:isSel?T.gold:isTaken?"rgba(100,160,255,.06)":"rgba(100,160,255,.13)",border:`1.5px solid ${isSel?T.gold:isTaken?"rgba(100,160,255,.15)":"rgba(100,160,255,.38)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:isSel?T.navy:"rgba(120,180,255,.65)",cursor:isTaken?"default":"pointer",opacity:isTaken?.35:1,transition:"all .15s",transform:isSel?"scale(1.15)":"scale(1)"}}>
                              {isSel?<span style={{fontSize:6,fontWeight:800}}>✓</span>:"♿"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{display:"flex",gap:1,alignItems:"flex-end"}}>
                      {Array.from({length:COLS},(_,i)=>{
                        const id=`${row}${i+1}`;
                        const isTaken=TAKEN.has(id);
                        const isSel=selected.includes(id);
                        const isPrem=CENTER.includes(id);
                        const isPOV=povSeat===id;
                        return (
                          <div key={i} style={{display:"flex",alignItems:"flex-end",marginLeft:isPairGap(i)?4:0}}>
                            <div onClick={()=>toggle(id)} style={{cursor:isTaken?"default":"pointer",display:"flex",flexDirection:"column",alignItems:"center",opacity:isTaken?.3:1,transition:"all .15s",transform:isSel?"scale(1.15)":"scale(1)"}}>
                              <div style={{width:13,height:4,borderRadius:"3px 3px 0 0",background:isSel?T.gold:isPrem?"rgba(139,92,246,.6)":T.navyLight,border:`1px solid ${isSel?T.gold:isPrem?"rgba(139,92,246,.5)":isPOV?T.goldBorder:T.greyDim}`,borderBottom:"none"}}/>
                              <div style={{width:17,height:15,borderRadius:"2px 2px 3px 3px",marginTop:-1,background:isSel?T.gold:isTaken?T.navyMid:isPrem?"rgba(139,92,246,.42)":T.navyLight,border:`1px solid ${isSel?T.gold:isTaken?"transparent":isPOV?T.goldBorder:isPrem?"rgba(139,92,246,.5)":T.greyDim}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                                {isTaken
                                  ? <span style={{fontSize:7,color:T.greyDim}}>×</span>
                                  : <span style={{fontSize:5,color:isSel?T.navy:T.grey,fontWeight:700,lineHeight:1}}>{row}{i+1}</span>
                                }
                              </div>
                              <div style={{width:20,height:3,borderRadius:"0 0 2px 2px",background:isSel?"rgba(201,168,76,.4)":T.greyDim}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <span style={{fontSize:9,color:T.grey,width:10,flexShrink:0,marginLeft:2}}>{row}</span>
                </div>
              );
              if (section) {
                return [
                  <div key={`sec-${row}`} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px 4px",width:"100%"}}>
                    <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.glassBorder},transparent)`}}/>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:8,fontWeight:700,color:T.goldLight,letterSpacing:".14em"}}>{section.label}</div>
                      <div style={{fontSize:7,color:T.grey,marginTop:1}}>{section.sub}</div>
                    </div>
                    <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.glassBorder},transparent)`}}/>
                  </div>,
                  seatRow,
                ];
              }
              return [seatRow];
            })}
          </div>
          {/* Exit doors at back of theater */}
          <div style={{marginTop:10,display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"0 14px"}}>
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{width:14,height:18,borderRadius:3,background:"rgba(39,174,96,.12)",border:"1.5px solid rgba(39,174,96,.35)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="rgba(39,174,96,.8)" strokeWidth="2.5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
              </div>
              <span style={{fontSize:7,color:"rgba(39,174,96,.65)",fontWeight:700,letterSpacing:".08em"}}>EXIT</span>
            </div>
            <div style={{flex:1,borderTop:`1px dashed ${T.greyDim}`,margin:"0 4px"}}/>
            <span style={{fontSize:7,color:T.grey,letterSpacing:".12em"}}>BACK OF THEATER</span>
            <div style={{flex:1,borderTop:`1px dashed ${T.greyDim}`,margin:"0 4px"}}/>
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={{fontSize:7,color:"rgba(39,174,96,.65)",fontWeight:700,letterSpacing:".08em"}}>EXIT</span>
              <div style={{width:14,height:18,borderRadius:3,background:"rgba(39,174,96,.12)",border:"1.5px solid rgba(39,174,96,.35)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="rgba(39,174,96,.8)" strokeWidth="2.5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{padding:"10px 16px 16px",display:"flex",flexWrap:"wrap",gap:10,justifyContent:"center",borderTop:`1px solid ${T.glassBorder}`}}>
          {[
            {color:T.navyLight,border:T.greyDim,label:"Available"},
            {color:T.gold,border:T.gold,label:"Selected"},
            {color:T.navyMid,border:"transparent",label:"Occupied"},
            {color:"rgba(139,92,246,.42)",border:"rgba(139,92,246,.5)",label:"Center"},
            {color:"rgba(100,160,255,.13)",border:"rgba(100,160,255,.38)",label:"Accessible",round:true},
            {color:"rgba(39,174,96,.12)",border:"rgba(39,174,96,.35)",label:"Exit",isExit:true},
          ].map(({color,border,label,round,isExit})=>(
            <div key={label} style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{width:round?11:isExit?10:13,height:round?11:isExit?12:10,borderRadius:round?"50%":isExit?"2px":"3px 3px 2px 2px",background:color,border:`1px solid ${border}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {isExit && <svg width="6" height="6" viewBox="0 0 24 24" fill="none" stroke="rgba(39,174,96,.8)" strokeWidth="3"><path d="M16 17l5-5-5-5M21 12H9"/></svg>}
              </div>
              <span style={{fontSize:10,color:T.grey}}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Per-seat POV card strip — one compact card per selected seat, horizontally scrollable ── */}
      {selected.length > 0 && (
        <div style={{flexShrink:0,borderTop:`1px solid ${T.goldBorder}`,background:T.navyMid}}>
          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
            <div style={{display:"flex",gap:8,padding:"10px 12px",width:"max-content"}}>
              {selected.map(seatId => {
                const isActive = povSeat === seatId;
                const zone = rowZone(seatId[0]);
                return (
                  <div key={seatId} onClick={()=>setPovSeat(seatId)}
                    style={{width:108,flexShrink:0,borderRadius:10,overflow:"hidden",border:`2px solid ${isActive?T.gold:T.glassBorder}`,cursor:"pointer",background:T.navyLight,transition:"border-color .15s",boxShadow:isActive?`0 0 10px rgba(201,168,76,.3)`:"none"}}>
                    <div onClick={e=>{e.stopPropagation();setExpandedPOV(seatId);}} style={{cursor:"zoom-in",position:"relative"}}>
                      <MiniPOV seatId={seatId} isActive={isActive}/>
                      <div style={{position:"absolute",top:3,right:3,width:14,height:14,borderRadius:3,background:"rgba(4,8,26,.7)",border:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={T.offwhite} strokeWidth="2.5"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                      </div>
                    </div>
                    <div style={{padding:"6px 8px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:isActive?T.gold:T.white}}>Seat {seatId}</div>
                        <div style={{fontSize:8,color:T.grey}}>{zone} · {distFor(zone)}</div>
                        <div style={{fontSize:7,color:T.grey,opacity:.7,marginTop:1}}>{angleFor(zone)}</div>
                      </div>
                      <button
                        onClick={e=>{e.stopPropagation();setSelected(s=>s.filter(x=>x!==seatId));if(povSeat===seatId)setPovSeat(selected.filter(x=>x!==seatId)[0]||null);}}
                        style={{background:"rgba(192,57,43,.15)",border:"1px solid rgba(192,57,43,.3)",borderRadius:6,width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#e74c3c",fontSize:13,flexShrink:0,lineHeight:1}}>×</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Expanded POV overlay — enlarged version of MiniPOV ── */}
      {expandedPOV && (
        <div onClick={()=>setExpandedPOV(null)} style={{position:"absolute",inset:0,zIndex:80,background:"rgba(2,4,8,.85)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",animation:"fadeIn .2s ease",cursor:"pointer"}}>
          <div style={{width:"90%",maxWidth:320,borderRadius:16,overflow:"hidden",border:`2px solid ${T.gold}`,boxShadow:`0 0 30px rgba(201,168,76,.2)`}} onClick={e=>e.stopPropagation()}>
            <MiniPOV seatId={expandedPOV} isActive={true} large={true}/>
            <div style={{background:T.navyLight,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:T.gold}}>Seat {expandedPOV}</div>
                <div style={{fontSize:10,color:T.grey,marginTop:2}}>{rowZone(expandedPOV[0])} · {distFor(rowZone(expandedPOV[0]))} · {angleFor(rowZone(expandedPOV[0]))}</div>
              </div>
              <button onClick={()=>setExpandedPOV(null)} style={{background:T.navyMid,border:`1px solid ${T.greyDim}`,borderRadius:8,width:30,height:30,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:14}}>×</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer — ALWAYS visible ── */}
      <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"12px 16px 16px"}}>
        {selected.length > 0 ? (
          <>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:T.white}}>{selected.length} Seat{selected.length>1?"s":""} Selected</div>
                <div style={{fontSize:10,color:T.grey,marginTop:1}}>{selected.join(", ")}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:16,fontWeight:700,color:T.gold}}>${totalEst.toFixed(2)}</div>
                <div style={{fontSize:9,color:T.grey}}>${seatPrice.toFixed(2)}/ea + ${SERVICE_FEE_GLOBAL.toFixed(2)} fee</div>
                {showtime?.isMatinee && <div style={{fontSize:8,color:T.green,fontWeight:700,marginTop:1}}>✦ Matinee pricing</div>}
              </div>
            </div>
            <button className="btn-gold" style={{width:"100%",padding:13,fontSize:14}} onClick={()=>{setSeatData(selected);nav("ticket-qty");}}>
              Continue to Tickets →
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// TICKET QUANTITY
// ══════════════════════════════════════════════════════
const TicketQtyScreen = ({ showtime, seats, nav, setTicketOrder, seatTimer }) => {
  const tLeft = seatTimer||0;
  const tStr = `${Math.floor(tLeft/60)}:${String(tLeft%60).padStart(2,'0')}`;
  const tUrgent = tLeft < 120;
  const AP = showtime?.basePrice || FORMAT_PRICING[showtime?.fmt] || 16.50;
  const CP = Math.max(AP - 5.50, 8.00); // child discount off adult
  const SP = Math.max(AP - 3.50, 10.00); // senior discount off adult
  const FEE = SERVICE_FEE_GLOBAL;
  const maxTickets = seats?.length || 0;
  const [adult, setAdult] = useState(0);
  const [child, setChild] = useState(0);
  const [senior, setSenior] = useState(0);
  const totalTickets = adult+child+senior;
  const remaining = maxTickets - totalTickets;
  const atMax = totalTickets >= maxTickets;
  const sub = adult*AP + child*CP + senior*SP;
  const feeTotal = FEE * totalTickets;

  const TicketRow = ({label, subLabel, price, val, setVal, note, icon}) => {
    return (
    <div style={{margin:"0 16px 10px",background:val>0?`linear-gradient(135deg,rgba(201,168,76,.06),rgba(201,168,76,.02))`:T.navyCard,border:`1px solid ${val>0?T.goldBorder:T.glassBorder}`,borderRadius:14,padding:"14px 16px",transition:"all .2s"}}>
      <div style={{display:"flex",alignItems:"center",gap:14}}>
        {/* Ticket type icon */}
        <div style={{width:42,height:42,borderRadius:10,background:val>0?T.goldDim:T.navyLight,border:`1px solid ${val>0?T.goldBorder:T.greyDim}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0,transition:"all .2s"}}>
          {icon||"🎟️"}
        </div>
        {/* Label + price */}
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"baseline",gap:8}}>
            <span style={{fontSize:15,fontWeight:700,color:T.white}}>{label}</span>
            {subLabel&&<span style={{fontSize:10,color:T.grey}}>{subLabel}</span>}
          </div>
          <div style={{fontSize:14,fontWeight:700,color:val>0?T.gold:T.offwhite,marginTop:2}}>${price.toFixed(2)}<span style={{fontSize:10,color:T.grey,fontWeight:400}}> /ticket</span></div>
        </div>
        {/* Compact stepper */}
        <div style={{display:"flex",alignItems:"center",gap:0,background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,overflow:"hidden",flexShrink:0}}>
          <button onClick={()=>setVal(Math.max(0,val-1))} style={{width:32,height:34,background:"transparent",border:"none",cursor:val>0?"pointer":"default",color:val>0?T.white:T.greyDim,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,transition:"color .15s",fontFamily:"'Outfit',sans-serif"}}>−</button>
          <span style={{width:28,textAlign:"center",fontSize:16,fontWeight:700,color:val>0?T.gold:T.white,fontVariantNumeric:"tabular-nums"}}>{val}</span>
          <button onClick={()=>{if(!atMax)setVal(val+1);}} style={{width:32,height:34,background:"transparent",border:"none",cursor:atMax?"default":"pointer",color:atMax?T.greyDim:T.white,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,transition:"color .15s",fontFamily:"'Outfit',sans-serif"}}>+</button>
        </div>
      </div>
      {note && <div style={{fontSize:10,color:T.gold,marginTop:8,paddingTop:8,borderTop:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",gap:5}}>ⓘ {note}</div>}
    </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>

      {/* ── Context header ── */}
      <div style={{flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <div style={{padding:"12px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
          <button onClick={()=>nav("seat-map")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0,marginTop:2}}>←</button>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white}}>{showtime?.movie?.title}</div>
            <div style={{fontSize:11,color:T.grey,marginTop:2}}>{showtime?.theater?.name} · {showtime?.date} · {showtime?.time} · {showtime?.fmt}</div>
          </div>
          <button onClick={()=>nav("movies")} style={{background:"none",border:"none",color:T.grey,fontSize:20,cursor:"pointer",flexShrink:0}}>×</button>
        </div>
        {/* Timer + Rewards row */}
        <div style={{borderTop:`1px solid ${T.glassBorder}`,padding:"6px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",background:tUrgent?"rgba(192,57,43,.08)":`linear-gradient(135deg,rgba(201,168,76,.08),rgba(201,168,76,.03))`}}>
          <div style={{fontSize:10,color:T.offwhite}}>⭐ 5,000 pts = $5 reward</div>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={tUrgent?T.red:T.grey} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span style={{fontSize:11,fontWeight:700,color:tUrgent?T.red:T.goldLight,fontVariantNumeric:"tabular-nums",animation:tUrgent?"pulse 1s ease-in-out infinite":"none"}}>{tStr}</span>
          </div>
        </div>
        {/* Matinee discount banner */}
        {showtime?.isMatinee && (
          <div style={{background:"rgba(39,174,96,.08)",borderTop:`1px solid rgba(39,174,96,.2)`,padding:"6px 16px",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:10,color:T.green,fontWeight:700}}>✦ MATINEE PRICING</span>
            <span style={{fontSize:10,color:T.grey}}>— $3.00 off each ticket</span>
          </div>
        )}
      </div>

      {/* ── Scrollable body ── */}
      <div style={{flex:1,overflowY:"auto"}}>

        {/* Seats selected subheader */}
        {seats?.length>0 && (
          <div style={{padding:"14px 16px 10px",background:T.navyCard,borderBottom:`1px solid ${T.glassBorder}`}}>
            <div style={{fontSize:14,fontWeight:700,color:remaining>0?T.white:T.green,textAlign:"center",marginBottom:2,transition:"color .2s"}}>
              {totalTickets===0
                ? `Assign ${maxTickets} ticket${maxTickets>1?"s":""} to your seats`
                : remaining>0
                  ? `${remaining} more ticket${remaining>1?"s":""} to assign`
                  : `All ${maxTickets} tickets assigned ✓`}
            </div>
            <div style={{fontSize:11,color:T.grey,textAlign:"center"}}>Seats: {seats.join(", ")}</div>
          </div>
        )}

        <div style={{paddingTop:10}}/>
        <TicketRow label="Adult" price={AP} val={adult} setVal={setAdult} icon="🎬"/>
        <TicketRow label="Child" subLabel="Ages 2–12" price={CP} val={child} setVal={setChild} icon="🧒"/>
        <TicketRow label="Senior" subLabel="Ages 60+" price={SP} val={senior} setVal={setSenior} icon="👤"/>

        {/* Notes + service fee */}
        <div style={{padding:"6px 16px 14px"}}>
          <button style={{background:"none",border:"none",cursor:"pointer",color:T.gold,fontSize:11,fontWeight:600,fontFamily:"'Outfit',sans-serif",display:"flex",alignItems:"center",gap:5,padding:0,marginBottom:8}}>
            ⓘ ID Required for R-Rated Movies
          </button>
          {totalTickets>0 && (
            <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:10,padding:"10px 14px"}}>
              <div style={{fontSize:10,color:T.grey,display:"flex",alignItems:"center",gap:4}}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                Each ticket includes a <span style={{color:T.offwhite,fontWeight:600}}>${FEE.toFixed(2)}</span> service fee — no hidden charges
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer — always visible ── */}
      <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"12px 16px 16px"}}>
        {totalTickets>0 ? (
          <>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
              <span style={{fontSize:12,fontWeight:700,color:T.offwhite,letterSpacing:".05em"}}>{totalTickets}/{maxTickets} TICKET{totalTickets>1?"S":""}</span>
              <span style={{fontSize:16,fontWeight:700,color:T.gold}}>${(sub+feeTotal).toFixed(2)}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <span style={{fontSize:9,color:T.grey}}>Seats: {seats?.join(", ")}</span>
              <span style={{fontSize:9,color:T.grey}}>All fees included · No surprises</span>
            </div>
            <button className="btn-gold" disabled={remaining!==0}
              style={{width:"100%",padding:13,fontSize:14,opacity:remaining!==0?.45:1,cursor:remaining!==0?"default":"pointer"}}
              onClick={()=>{if(remaining===0){setTicketOrder({adult,child,senior,total:sub,feeTotal});nav("ticket-checkout");}}}>
              {remaining>0 ? `Assign ${remaining} more ticket${remaining>1?"s":""}` : "Continue to Checkout →"}
            </button>
          </>
        ) : (
          <div style={{textAlign:"center",padding:"4px 0"}}>
            <div style={{fontSize:12,color:T.grey}}>Assign a ticket type to each of your {maxTickets} seat{maxTickets>1?"s":""}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// TICKET CHECKOUT
// ══════════════════════════════════════════════════════
const TicketCheckoutScreen = ({ showtime, seats, ticketOrder, nav, seatTimer }) => {
  const tLeft = seatTimer||0;
  const tStr = `${Math.floor(tLeft/60)}:${String(tLeft%60).padStart(2,'0')}`;
  const tUrgent = tLeft < 120;
  const [pay, setPay] = useState("apple");
  const [promoOpen, setPromoOpen] = useState(false);
  const [promo, setPromo] = useState("");
  const [promoOK, setPromoOK] = useState(false);
  const [feeSheetOpen, setFeeSheetOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [rewardApplied, setRewardApplied] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const FEE = SERVICE_FEE_GLOBAL;
  const ticketCount = (ticketOrder?.adult||0)+(ticketOrder?.child||0)+(ticketOrder?.senior||0);
  const basePrice = showtime?.basePrice || FORMAT_PRICING[showtime?.fmt] || 16.50;
  const sub = ticketOrder?.total || (basePrice * ticketCount);
  const fees = FEE * Math.max(ticketCount,1);
  const rewardBalance = 5240;
  const rewardValue = Math.floor(rewardBalance / 5000) * 5; // $5 per 5000 pts
  const rewardDisc = rewardApplied ? rewardValue : 0;
  const disc = promoOK ? (sub*0.10) : 0;
  const total = Math.max(0, sub + fees - rewardDisc - disc);
  const ptsEarned = rewardApplied ? 0 : Math.round(total * 10);

  const seatList = seats?.join(", ") || "";
  const PREMIUM_ROWS_CK = ["E","F","G"];
  const zoneFor = id => { const r = id?.charAt(0); if(["A","B","C"].includes(r)) return "Front"; if(PREMIUM_ROWS_CK.includes(r)) return "Center"; return "Balcony"; };
  const distFor = z => z==="Front"?"~15 ft":z==="Center"?"~35 ft":"~55 ft";
  const angleFor = z => z==="Front"?"Front rows":z==="Center"?"Center rows":"Elevated";

  const PAYMENT_OPTS = [
    {id:"apple", label:"Apple Pay", icon:"🍎"},
    {id:"gpay",  label:"Google Pay", icon:"G"},
    {id:"paypal",label:"PayPal", icon:"P"},
    {id:"venmo", label:"Venmo", icon:"V"},
    {id:"card",  label:"Credit / Debit Card", icon:"💳"},
  ];

  // Mini seat map for checkout
  const MINI_ROWS = ["D","E","F","G","H"];
  const MINI_COLS = 10;
  const CheckoutMiniMap = () => (
    <div style={{background:"rgba(4,8,15,.6)",borderRadius:10,padding:"8px 8px 6px",marginBottom:0}}>
      <div style={{textAlign:"center",marginBottom:3}}>
        <svg width="70" height="6" viewBox="0 0 70 6" fill="none" style={{display:"block",margin:"0 auto"}}>
          <path d="M2 5 Q35 0 68 5" stroke={T.gold} strokeWidth=".8" fill="none" opacity=".4"/>
        </svg>
        <div style={{fontSize:5,color:T.grey,letterSpacing:".12em",marginTop:1}}>SCREEN</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:1.5,alignItems:"center"}}>
        {MINI_ROWS.map(row=>(
          <div key={row} style={{display:"flex",gap:1.2,alignItems:"center"}}>
            <span style={{fontSize:5,color:T.grey,width:7,textAlign:"right"}}>{row}</span>
            {Array.from({length:MINI_COLS},(_,i)=>{
              const id=`${row}${i+2}`;
              const isMine = seats?.includes(id);
              const isPrem = PREMIUM_ROWS_CK.includes(row);
              return <div key={i} style={{width:5.5,height:4.5,borderRadius:1,background:isMine?T.gold:isPrem?"rgba(147,112,219,.25)":"rgba(255,255,255,.06)",border:`.5px solid ${isMine?T.gold:isPrem?"rgba(147,112,219,.15)":"rgba(255,255,255,.04)"}`,boxShadow:isMine?`0 0 4px ${T.gold}`:"none"}}/>;
            })}
          </div>
        ))}
      </div>
      {seats?.length>0&&<div style={{textAlign:"center",marginTop:3,fontSize:5.5,color:T.goldLight,fontWeight:700,letterSpacing:".06em"}}>YOUR SEAT{seats.length>1?"S":""}: {seatList}</div>}
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0,position:"relative"}}>
      {/* Fee explanation sheet */}
      {feeSheetOpen && (
        <div style={{position:"absolute",inset:0,zIndex:400,display:"flex",flexDirection:"column"}}>
          <div onClick={()=>setFeeSheetOpen(false)} style={{flex:1,background:"rgba(0,0,0,.6)"}}/>
          <div style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:"24px 20px 40px"}}>
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{width:36,height:4,borderRadius:2,background:"#ddd",margin:"0 auto 16px"}}/>
              <div style={{fontSize:16,fontWeight:800,color:"#111",letterSpacing:".06em"}}>SERVICE FEE</div>
            </div>
            <p style={{fontSize:14,color:"#444",lineHeight:1.6,marginBottom:20}}>Includes service and transaction fees for secure credit card processing and mobile ticket delivery.</p>
            <div style={{borderTop:"1px solid #eee",paddingTop:16}}>
              <div style={{fontSize:12,fontWeight:800,color:"#111",letterSpacing:".08em",marginBottom:12}}>DETAILS</div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #eee"}}>
                <span style={{fontSize:14,color:"#444"}}>Tickets ({ticketCount})</span>
                <span style={{fontSize:14,color:"#111",fontWeight:600}}>${fees.toFixed(2)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",paddingTop:12}}>
                <span style={{fontSize:15,fontWeight:800,color:"#111"}}>Total Fees</span>
                <span style={{fontSize:15,fontWeight:800,color:"#111"}}>${fees.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── "Before You Buy" confirmation sheet ── */}
      {summaryOpen && (
        <div style={{position:"absolute",inset:0,zIndex:400,display:"flex",flexDirection:"column"}}>
          <div onClick={()=>setSummaryOpen(false)} style={{flex:1,background:"rgba(0,0,0,.55)"}}/>
          <div style={{background:T.navyMid,borderRadius:"20px 20px 0 0",padding:"20px 20px 32px",border:`1px solid ${T.glassBorder}`,borderBottom:"none"}}>
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{width:36,height:4,borderRadius:2,background:T.greyDim,margin:"0 auto 14px"}}/>
              <div style={{fontSize:14,fontWeight:700,color:T.white,fontFamily:"'Playfair Display',serif"}}>Before You Buy</div>
              <div style={{fontSize:10,color:T.grey,marginTop:3}}>Please review your order</div>
            </div>
            {/* Summary grid */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {[
                ["🎬",showtime?.movie?.title||"Movie"],
                ["📅",`${showtime?.date||"Today"} · ${showtime?.time||""}`],
                ["🎭",`${showtime?.theater?.short||showtime?.theater?.name||"Theater"}`],
                ["💺",`${seatList} (${ticketCount} ticket${ticketCount!==1?"s":""})`],
                ["📽️",showtime?.fmt||"Standard"],
                ["💰",`$${total.toFixed(2)} total`],
              ].map(([icon,val],i)=>(
                <div key={i} style={{background:T.navyCard,borderRadius:10,padding:"10px 12px",border:`1px solid ${T.glassBorder}`}}>
                  <div style={{fontSize:14,marginBottom:3}}>{icon}</div>
                  <div style={{fontSize:11,color:T.white,fontWeight:500,lineHeight:1.4}}>{val}</div>
                </div>
              ))}
            </div>
            {rewardApplied&&<div style={{textAlign:"center",fontSize:11,color:T.gold,fontWeight:600,marginBottom:12}}>🎁 ${rewardDisc.toFixed(2)} reward applied</div>}
            <button className="btn-gold" style={{width:"100%",padding:14,fontSize:14,borderRadius:50}} onClick={()=>{setSummaryOpen(false);nav("ticket-confirm");}}>
              Confirm Purchase · ${total.toFixed(2)}
            </button>
            <button onClick={()=>setSummaryOpen(false)} style={{width:"100%",padding:10,fontSize:12,color:T.grey,background:"none",border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",marginTop:6}}>Go Back</button>
          </div>
        </div>
      )}

      {/* ── Context header ── */}
      <div style={{flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <div style={{padding:"12px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
          <button onClick={()=>nav("ticket-qty")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0,marginTop:2}}>←</button>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white}}>{showtime?.movie?.title}</div>
            <div style={{fontSize:11,color:T.grey,marginTop:2}}>{showtime?.theater?.name} · {showtime?.date} · {showtime?.time} · {showtime?.fmt}</div>
          </div>
          <button onClick={()=>nav("movies")} style={{background:"none",border:"none",color:T.grey,fontSize:20,cursor:"pointer",flexShrink:0}}>×</button>
        </div>
        {/* Timer bar */}
        <div style={{padding:"5px 16px 6px",borderTop:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:tUrgent?"rgba(192,57,43,.08)":"rgba(201,168,76,.04)"}}>
          <span style={{fontSize:9,color:T.grey}}>Seats held for</span>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={tUrgent?T.red:T.grey} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span style={{fontSize:10,fontWeight:700,color:tUrgent?T.red:T.goldLight,fontVariantNumeric:"tabular-nums",animation:tUrgent?"pulse 1s ease-in-out infinite":"none"}}>{tStr}</span>
          </div>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div style={{flex:1,overflowY:"auto"}}>
      <div style={{padding:"14px 16px"}}>

        {/* ── Movie summary card ── */}
        <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:14,marginBottom:14,display:"flex",gap:12}}>
          <div style={{width:60,height:84,borderRadius:8,overflow:"hidden",flexShrink:0,border:`1px solid ${T.glassBorder}`}}><MoviePoster movieId={showtime?.movie?.id||1} width={60} height={84} mini/></div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:T.white,marginBottom:2}}>{showtime?.movie?.title}</div>
            <div style={{fontSize:11,fontWeight:600,color:T.offwhite,marginBottom:6}}>{showtime?.date} at {showtime?.time}</div>
            <div style={{fontSize:11,color:T.grey,marginBottom:8}}>{showtime?.theater?.name}</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {[showtime?.movie?.rating,"STADIUM SEATING","CC",showtime?.fmt||"STANDARD"].map(tag=>tag&&(
                <span key={tag} style={{fontSize:9,fontWeight:700,color:T.grey,background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:4,padding:"2px 5px",letterSpacing:".04em"}}>{tag}</span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Your Seats — mini map + POV ── */}
        <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:14,marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:T.grey,textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>Your Seats</div>
          <div style={{display:"flex",gap:12}}>
            <div style={{flex:1}}><CheckoutMiniMap/></div>
            {/* POV info for each selected seat */}
            <div style={{flex:1,display:"flex",flexDirection:"column",gap:6,justifyContent:"center"}}>
              {(seats||[]).slice(0,4).map(s=>{
                const zone = zoneFor(s);
                return (
                  <div key={s} style={{background:"rgba(255,255,255,.03)",borderRadius:8,padding:"6px 8px",border:`1px solid ${T.glassBorder}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                      <span style={{fontSize:11,fontWeight:700,color:T.goldLight}}>{s}</span>
                      <span style={{fontSize:8,color:PREMIUM_ROWS_CK.includes(s?.charAt(0))?"rgba(147,112,219,.9)":T.grey,fontWeight:600}}>{zone.toUpperCase()}</span>
                    </div>
                    <div style={{fontSize:8,color:T.grey,marginTop:2}}>{distFor(zone)} · {angleFor(zone)}</div>
                  </div>
                );
              })}
              {seats?.length>4&&<div style={{fontSize:9,color:T.grey,textAlign:"center"}}>+{seats.length-4} more</div>}
            </div>
          </div>
        </div>

        {/* ── Savings & Rewards — compact card ── */}
        <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
          {/* Reward toggle */}
          <div style={{padding:"12px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:promoOpen||rewardApplied?`1px solid ${T.glassBorder}`:"none"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:14}}>⭐</span>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:T.white}}>{rewardBalance.toLocaleString()} pts</div>
                <div style={{fontSize:9,color:T.grey}}>${rewardValue.toFixed(2)} available to redeem</div>
              </div>
            </div>
            {rewardValue>0&&(
              <button onClick={()=>setRewardApplied(!rewardApplied)} style={{background:rewardApplied?T.gold:"transparent",border:`2px solid ${rewardApplied?T.gold:T.greyDim}`,borderRadius:20,width:44,height:24,cursor:"pointer",position:"relative",transition:"all .2s",flexShrink:0}}>
                <div style={{width:18,height:18,borderRadius:"50%",background:rewardApplied?"#fff":T.grey,position:"absolute",top:1,left:rewardApplied?22:1,transition:"all .2s",boxShadow:"0 1px 3px rgba(0,0,0,.3)"}}/>
              </button>
            )}
          </div>
          {rewardApplied&&<div style={{padding:"8px 14px",fontSize:10,color:T.green,fontWeight:600,background:"rgba(39,174,96,.06)"}}>✓ ${rewardDisc.toFixed(2)} reward applied</div>}
          {/* Promo code inline */}
          <div style={{padding:"0 14px"}}>
            <button onClick={()=>setPromoOpen(!promoOpen)} style={{width:"100%",background:"none",border:"none",cursor:"pointer",padding:"10px 0",display:"flex",alignItems:"center",justifyContent:"space-between",fontFamily:"'Outfit',sans-serif"}}>
              <span style={{fontSize:12,color:T.offwhite,fontWeight:500}}>Promo Code or Voucher</span>
              <span style={{color:T.grey,fontSize:12,transform:promoOpen?"rotate(180deg)":"none",transition:"transform .2s"}}>▾</span>
            </button>
            {promoOpen&&(
              <div style={{paddingBottom:12,display:"flex",gap:8}}>
                <input className="inp" value={promo} onChange={e=>setPromo(e.target.value)} placeholder="Enter code" style={{flex:1,padding:"9px 12px",fontSize:12}}/>
                <button className="btn-outline" style={{padding:"9px 12px",fontSize:11,whiteSpace:"nowrap"}} onClick={()=>promo.length>2&&setPromoOK(true)}>Apply</button>
              </div>
            )}
          </div>
        </div>

        {/* ── Payment — selectable cards instead of radio list ── */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Payment Method</div>
          {/* Quick-pay row */}
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            {PAYMENT_OPTS.filter(o=>o.id!=="card").map(opt=>(
              <button key={opt.id} onClick={()=>setPay(opt.id)} style={{flex:1,padding:"10px 4px",borderRadius:10,background:pay===opt.id?T.goldDim:T.navyCard,border:`1.5px solid ${pay===opt.id?T.goldBorder:T.glassBorder}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,transition:"all .15s"}}>
                <span style={{fontSize:16}}>{opt.icon}</span>
                <span style={{fontSize:8,fontWeight:600,color:pay===opt.id?T.goldLight:T.grey,fontFamily:"'Outfit',sans-serif"}}>{opt.label.split(" ")[0]}</span>
              </button>
            ))}
          </div>
          {/* Card option */}
          <button onClick={()=>setPay("card")} style={{width:"100%",padding:"10px 14px",borderRadius:10,background:pay==="card"?T.goldDim:T.navyCard,border:`1.5px solid ${pay==="card"?T.goldBorder:T.glassBorder}`,cursor:"pointer",display:"flex",alignItems:"center",gap:10,transition:"all .15s"}}>
            <span style={{fontSize:16}}>💳</span>
            <span style={{fontSize:12,fontWeight:500,color:pay==="card"?T.goldLight:T.offwhite,fontFamily:"'Outfit',sans-serif"}}>Credit / Debit Card</span>
          </button>
          {pay==="card"&&(
            <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
              <input className="inp" placeholder="Card Number"/>
              <div style={{display:"flex",gap:8}}>
                <input className="inp" placeholder="MM/YY" style={{flex:1}}/>
                <input className="inp" placeholder="CVV" style={{flex:1}}/>
              </div>
              <input className="inp" placeholder="Name on Card"/>
            </div>
          )}
        </div>

        {/* ── Contact + notices — grouped compact ── */}
        <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:"12px 14px",marginBottom:14}}>
          <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:8}}>Delivery</div>
          <input className="inp" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email for e-ticket delivery" style={{marginBottom:4,fontSize:12,padding:"9px 12px"}}/>
          <div style={{fontSize:9,color:T.grey}}>Receipt and mobile ticket sent here</div>
        </div>

        {/* Info notices — collapsed into icons */}
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          <div style={{flex:1,background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:10,padding:"10px 12px"}}>
            <div style={{fontSize:14,marginBottom:4}}>🎬</div>
            <div style={{fontSize:10,fontWeight:600,color:T.offwhite,marginBottom:2}}>Trailers First</div>
            <div style={{fontSize:8,color:T.grey,lineHeight:1.4}}>Feature starts 25–30 min after listed time</div>
          </div>
          <div style={{flex:1,background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:10,padding:"10px 12px"}}>
            <div style={{fontSize:14,marginBottom:4}}>🪪</div>
            <div style={{fontSize:10,fontWeight:600,color:T.offwhite,marginBottom:2}}>R-Rated ID</div>
            <div style={{fontSize:8,color:T.grey,lineHeight:1.4}}>Under 17 must have 21+ guardian</div>
          </div>
        </div>

        {/* ── Order summary — compact card ── */}
        <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:"14px 16px",marginBottom:16}}>
          <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Order Summary</div>
          {/* Compact ticket lines */}
          {ticketOrder?.adult>0&&(
            <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:12}}>
              <span style={{color:T.offwhite}}>Adult × {ticketOrder.adult}</span>
              <span style={{color:T.white,fontWeight:600}}>${(basePrice*ticketOrder.adult).toFixed(2)}</span>
            </div>
          )}
          {ticketOrder?.child>0&&(
            <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:12}}>
              <span style={{color:T.offwhite}}>Child × {ticketOrder.child}</span>
              <span style={{color:T.white,fontWeight:600}}>${((basePrice-5.50)*ticketOrder.child).toFixed(2)}</span>
            </div>
          )}
          {ticketOrder?.senior>0&&(
            <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:12}}>
              <span style={{color:T.offwhite}}>Senior × {ticketOrder.senior}</span>
              <span style={{color:T.white,fontWeight:600}}>${((basePrice-3.50)*ticketOrder.senior).toFixed(2)}</span>
            </div>
          )}
          <div style={{height:1,background:T.glassBorder,margin:"8px 0"}}/>
          <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
            <span style={{color:T.grey}}>Service Fee ({ticketCount}×${FEE.toFixed(2)})</span>
            <span style={{color:T.grey}}>${fees.toFixed(2)}</span>
          </div>
          {rewardApplied&&(
            <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
              <span style={{color:T.gold}}>⭐ Reward</span>
              <span style={{color:T.gold}}>−${rewardDisc.toFixed(2)}</span>
            </div>
          )}
          {promoOK&&(
            <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
              <span style={{color:T.green}}>Promo (10% off)</span>
              <span style={{color:T.green}}>−${disc.toFixed(2)}</span>
            </div>
          )}
          <div style={{height:1,background:T.glassBorder,margin:"8px 0"}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
            <span style={{fontSize:14,fontWeight:700,color:T.white}}>Total</span>
            <span style={{fontSize:18,fontWeight:800,color:T.gold}}>${total.toFixed(2)}</span>
          </div>
          {ptsEarned>0&&<div style={{fontSize:9,color:T.gold,textAlign:"right",marginTop:2}}>+{ptsEarned} pts earned</div>}
        </div>

        {/* ── Terms ── */}
        <div style={{fontSize:9,color:T.grey,textAlign:"center",marginBottom:16,lineHeight:1.5}}>
          No refunds after showtime. By purchasing, I agree to the{" "}
          <span style={{color:T.gold,textDecoration:"underline",cursor:"pointer"}}>ReelView Terms of Use</span>.
        </div>
      </div>

      {/* ── Sticky purchase footer ── */}
      </div>{/* end scrollable body */}

      <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"12px 16px 18px"}}>
        {rewardApplied&&<div style={{fontSize:9,color:T.gold,textAlign:"center",marginBottom:6,fontWeight:600}}>⭐ ${rewardDisc.toFixed(2)} reward applied</div>}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <div>
              <div style={{fontSize:10,color:T.grey}}>{ticketCount} ticket{ticketCount!==1?"s":""} · {seatList}</div>
              <div style={{fontSize:15,fontWeight:700,color:T.gold}}>${total.toFixed(2)}</div>
            </div>
          </div>
          <button className="btn-gold" style={{padding:"13px 28px",fontSize:14,borderRadius:50}} onClick={()=>setSummaryOpen(true)}>
            Purchase
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// TICKET CONFIRMATION
// ══════════════════════════════════════════════════════
const TicketConfirmScreen = ({ showtime, seats, nav, prevScreen }) => {
  const code = "RV-"+Math.random().toString(36).substr(2,8).toUpperCase();
  const seatList = seats?.join(", ") || "F6, F7";
  const movieTitle = showtime?.movie?.title || "Wuthering Heights";
  const theaterShort = showtime?.theater?.short || "ReelView Grand";
  const timeStr = showtime?.time || "7:00 PM";
  const dateStr = showtime?.date || "Today";
  const shareMsg = `Hey! I just got tickets to ${movieTitle} — ${dateStr} at ${timeStr}, ${theaterShort}. I'm in seat${seats?.length>1?"s":""} ${seatList}. Grab a seat next to me! 🎬🍿`;
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invited, setInvited] = useState([]);
  const [sentConfirm, setSentConfirm] = useState(false);
  const CONTACTS = [
    {name:"Jordan Kim",initials:"JK",phone:"(213) 555-0194"},
    {name:"Maya Thompson",initials:"MT",phone:"(310) 555-0287"},
    {name:"Aiden Park",initials:"AP",phone:"(323) 555-0143"},
    {name:"Sofia Reyes",initials:"SR",phone:"(213) 555-0361"},
    {name:"Daniel Wu",initials:"DW",phone:"(310) 555-0428"},
  ];
  const toggleInv = n => setInvited(s => s.includes(n) ? s.filter(x=>x!==n) : [...s,n]);

  // Mini seat map for the invite card
  const MINI_ROWS = ["E","F","G","H"];
  const MINI_COLS = 8;
  const MiniSeatMapConfirm = () => (
    <div style={{background:"rgba(4,8,15,.6)",borderRadius:8,padding:"8px 6px 6px",marginBottom:12}}>
      {/* Mini screen arc */}
      <div style={{textAlign:"center",marginBottom:4}}>
        <svg width="80" height="8" viewBox="0 0 80 8" fill="none" style={{display:"block",margin:"0 auto"}}>
          <path d="M2 7 Q40 1 78 7" stroke={T.gold} strokeWidth="1" fill="none" opacity=".5"/>
        </svg>
        <div style={{fontSize:5,color:T.grey,letterSpacing:".15em",marginTop:1}}>SCREEN</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:2,alignItems:"center"}}>
        {MINI_ROWS.map(row=>(
          <div key={row} style={{display:"flex",gap:1.5,alignItems:"center"}}>
            <span style={{fontSize:5,color:T.grey,width:8,textAlign:"right"}}>{row}</span>
            {Array.from({length:MINI_COLS},(_,i)=>{
              const id=`${row}${i+3}`;
              const isMine = seats?.includes(id);
              return <div key={i} style={{width:6,height:5,borderRadius:1,background:isMine?T.gold:"rgba(255,255,255,.08)",border:`0.5px solid ${isMine?T.gold:"rgba(255,255,255,.06)"}`,transition:"all .2s"}}/>;
            })}
            <span style={{fontSize:5,color:T.grey,width:8}}>{row}</span>
          </div>
        ))}
      </div>
      {seats?.length>0 && (
        <div style={{textAlign:"center",marginTop:4,fontSize:6,color:T.goldLight,fontWeight:700,letterSpacing:".06em"}}>
          YOUR SEAT{seats.length>1?"S":""}: {seatList}
        </div>
      )}
    </div>
  );

  return (
    <div className="screen" style={{paddingBottom:40,background:T.navy}}>
      <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.glassBorder}`,display:"flex",alignItems:"center",gap:10}}>
        <button onClick={()=>nav(prevScreen==="myreelview"?"myreelview":"movies")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0}}>←</button>
        <div style={{flex:1,display:"flex",justifyContent:"center"}}><Logo size={0.9}/></div>
        <div style={{width:34}}/>
      </div>
      <div style={{padding:"16px 16px"}}>

        {/* ── Success header (only on fresh purchase, not when viewing later) ── */}
        {prevScreen!=="myreelview" && (
          <div style={{textAlign:"center",marginBottom:18}}>
            <div style={{width:48,height:48,borderRadius:"50%",background:`linear-gradient(135deg,${T.gold},${T.goldLight})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 10px",boxShadow:`0 4px 20px ${T.goldDim}`}}>
              <span style={{fontSize:22,color:T.navy}}>✓</span>
            </div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.white,marginBottom:4}}>You're all set!</div>
            <div style={{fontSize:12,color:T.grey}}>Confirmation sent to your email</div>
            <div style={{fontSize:11,color:T.gold,fontWeight:600,marginTop:4}}>+250 reward points earned ⭐</div>
          </div>
        )}

        {/* ── E-Ticket ── */}
        <div style={{background:`linear-gradient(145deg,${T.navyLight},${T.navyMid})`,border:`1px solid ${T.goldBorder}`,borderRadius:20,padding:20,marginBottom:18,position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-28,right:-28,width:100,height:100,borderRadius:"50%",background:T.goldDim,pointerEvents:"none"}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
            <Logo size={0.72}/>
            <span className="tag">E-TICKET</span>
          </div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:T.white,marginBottom:2}}>{movieTitle}</div>
          <div style={{fontSize:11,color:T.grey,marginBottom:14}}>{showtime?.movie?.genre||"Sci-Fi"} · {showtime?.movie?.rating||"PG-13"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {[["Date",dateStr],["Time",timeStr],["Theater",theaterShort],["Seats",seatList],["Format",showtime?.fmt||"Standard"],["Screen",SHOWROOMS[showtime?.showroom]?.name||"Auditorium"]].map(([k,v])=>(
              <div key={k}><div style={{fontSize:9,color:T.grey,marginBottom:1,letterSpacing:".07em",textTransform:"uppercase"}}>{k}</div><div style={{fontSize:12,fontWeight:600,color:T.white}}>{v}</div></div>
            ))}
          </div>
          {/* Tear line */}
          <div style={{display:"flex",alignItems:"center",gap:0,margin:"14px -20px"}}>
            <div style={{width:18,height:18,borderRadius:"50%",background:T.navy,flexShrink:0}}/>
            <div style={{flex:1,borderTop:`2px dashed ${T.greyDim}`}}/>
            <div style={{width:18,height:18,borderRadius:"50%",background:T.navy,flexShrink:0}}/>
          </div>
          {/* Barcode */}
          <div style={{textAlign:"center",paddingTop:2}}>
            <div style={{display:"flex",gap:1.5,justifyContent:"center",marginBottom:5}}>
              {Array.from({length:34},(_,i)=><div key={i} style={{width:i%5===0?3:i%3===0?2:1.5,height:40,background:T.white,borderRadius:1,opacity:.3+((i*17)%7)*.1}}/>)}
            </div>
            <div style={{fontSize:9,color:T.grey,letterSpacing:".18em"}}>{code}</div>
          </div>
        </div>

        {/* ── Invite a Friend ── */}
        <button className="btn-gold" style={{width:"100%",padding:14,fontSize:13,marginBottom:14,display:"flex",alignItems:"center",justifyContent:"center",gap:8}} onClick={()=>{setInviteOpen(true);setSentConfirm(false);setInvited([]);}}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
          Invite a Friend
        </button>

        {/* ── Invite Modal ── */}
        {inviteOpen && (
          <div style={{position:"absolute",inset:0,zIndex:300,display:"flex",flexDirection:"column"}}>
            <div onClick={()=>setInviteOpen(false)} style={{flex:1,background:"rgba(0,0,0,.6)"}}/>
            <div style={{background:T.navyMid,borderRadius:"20px 20px 0 0",maxHeight:"80%",display:"flex",flexDirection:"column"}}>
              {/* Modal header */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
                <span style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>Invite a Friend</span>
                <button onClick={()=>setInviteOpen(false)} style={{background:"none",border:"none",color:T.grey,fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
              </div>

              <div style={{overflowY:"auto",padding:"14px 20px 20px"}}>
                {sentConfirm ? (
                  /* ── Sent confirmation ── */
                  <div style={{textAlign:"center",padding:"20px 0"}}>
                    <div style={{width:52,height:52,borderRadius:"50%",background:"rgba(39,174,96,.15)",border:`2px solid ${T.green}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:22,animation:"scaleIn .3s ease"}}>✓</div>
                    <div style={{fontSize:15,fontWeight:700,color:T.white,marginBottom:4}}>Invite{invited.length>1?"s":""} sent!</div>
                    <div style={{fontSize:11,color:T.grey,lineHeight:1.5}}>Your pre-filled message was opened in your messaging app for {invited.join(" & ")}.</div>
                    <button className="btn-outline" style={{marginTop:16,padding:"10px 28px",fontSize:12}} onClick={()=>setInviteOpen(false)}>Done</button>
                  </div>
                ) : (
                  <>
                    {/* Pre-filled message preview */}
                    <div style={{background:"rgba(4,8,15,.5)",border:`1px solid ${T.glassBorder}`,borderRadius:10,padding:12,marginBottom:14}}>
                      <div style={{fontSize:8,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:5}}>PRE-FILLED MESSAGE</div>
                      <div style={{fontSize:11,color:T.offwhite,lineHeight:1.55,fontStyle:"italic"}}>"{shareMsg}"</div>
                    </div>

                    {/* Contact list header */}
                    <div style={{fontSize:10,fontWeight:700,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:10}}>Select Contacts</div>

                    {/* Contacts */}
                    {CONTACTS.map((c,i) => {
                      const on = invited.includes(c.name);
                      return (
                        <div key={i} onClick={()=>toggleInv(c.name)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 0",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
                          <div style={{display:"flex",alignItems:"center",gap:12}}>
                            <div style={{width:36,height:36,borderRadius:"50%",background:on?T.goldDim:T.navyLight,border:`1.5px solid ${on?T.goldBorder:T.greyDim}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:on?T.gold:T.grey,transition:"all .2s"}}>{c.initials}</div>
                            <div>
                              <div style={{fontSize:13,fontWeight:600,color:T.white}}>{c.name}</div>
                              <div style={{fontSize:10,color:T.grey}}>{c.phone}</div>
                            </div>
                          </div>
                          <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?T.gold:T.greyDim}`,background:on?T.gold:"transparent",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
                            {on&&<span style={{fontSize:12,color:T.navy,fontWeight:900}}>✓</span>}
                          </div>
                        </div>
                      );
                    })}

                    {/* Send button */}
                    <button className="btn-gold" disabled={invited.length===0} onClick={()=>setSentConfirm(true)}
                      style={{width:"100%",padding:14,fontSize:13,marginTop:16,opacity:invited.length===0?.4:1,display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                      <span>💬</span>
                      {invited.length===0?"Select a contact":"Send via Text Message"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Concessions upsell ── */}
        <div style={{background:`linear-gradient(135deg,rgba(255,160,50,.08),rgba(201,168,76,.04))`,border:`1px solid rgba(255,160,50,.2)`,borderRadius:16,padding:"14px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:36,flexShrink:0}}>🍿</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:T.white,marginBottom:2}}>Skip the line</div>
            <div style={{fontSize:10,color:T.grey,lineHeight:1.4,marginBottom:8}}>Pre-order snacks now and pick them up when you arrive — no waiting.</div>
            <button className="btn-outline" style={{padding:"8px 14px",fontSize:11}} onClick={()=>nav("concessions")}>
              Order Concessions →
            </button>
          </div>
        </div>

        {/* ── Secondary actions ── */}
        <div style={{display:"flex",gap:10,marginBottom:14}}>
          <button className="btn-outline" style={{flex:1,padding:11,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
            Add to Wallet
          </button>
          <button className="btn-outline" style={{flex:1,padding:11,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            📅 Add to Calendar
          </button>
        </div>

        {/* Back navigation handled by top-left arrow */}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// THEATERS
// ══════════════════════════════════════════════════════
// ── THEATER FILTER SHEET ──────────────────────────────
const TheaterFilterSheet = ({ onClose }) => {
  const [selected, setSelected] = useState(["All"]);
  const OPTIONS = ["All","IMAX","Dolby Atmos","Luxury Recliners","Dine-In Delivery","Reserved Seating","RealD 3D","Discount Matinees","Open Captions","Sensory Friendly","Wheelchair Access","Bar & Lounge","Free Parking","Military Pricing"];
  const toggle = v => {
    if(v==="All"){ setSelected(["All"]); return; }
    setSelected(s => { const n=s.filter(x=>x!=="All"); return n.includes(v)?n.filter(x=>x!==v)||["All"]:[...n,v]; });
  };
  return (
    <div style={{position:"absolute",inset:0,zIndex:300,display:"flex",flexDirection:"column"}}>
      <div onClick={onClose} style={{flex:1,background:"rgba(0,0,0,.55)"}}/>
      <div style={{background:T.navyMid,borderRadius:"20px 20px 0 0",maxHeight:"78%",display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${T.glassBorder}`}}>
          <span style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>Filter Theaters</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.grey,fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{overflowY:"auto",flex:1}}>
          {OPTIONS.map(opt=>{
            const on = selected.includes(opt);
            return (
              <div key={opt} onClick={()=>toggle(opt)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
                <span style={{fontSize:14,color:on?T.white:T.offwhite,fontWeight:on?600:400}}>{opt}</span>
                {on && <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-7" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
            );
          })}
        </div>
        <div style={{padding:"12px 20px 24px",borderTop:`1px solid ${T.glassBorder}`,display:"flex",gap:10}}>
          <button onClick={()=>setSelected(["All"])} style={{background:"none",border:"none",color:T.gold,fontFamily:"'Outfit',sans-serif",fontWeight:600,fontSize:14,cursor:"pointer"}}>Clear All</button>
          <button className="btn-gold" onClick={onClose} style={{flex:1,padding:13,fontSize:14}}>Show Theaters</button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// THEATERS LIST SCREEN
// ══════════════════════════════════════════════════════
const TheatersScreen = ({ nav, setTheater, setTheaterInitTab }) => {
  const [tab, setTab] = useState("near");
  const [favTheaters, setFavTheaters] = useState([2]);
  const [showFilter, setShowFilter] = useState(false);
  const toggleFav = (e,id) => { e.stopPropagation(); setFavTheaters(f=>f.includes(id)?f.filter(x=>x!==id):[...f,id]); };

  // Styled map node positions for each theater
  const PIN_POSITIONS = [[38,42],[62,28],[50,62]];

  return (
    <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",position:"relative"}}>
      {showFilter && <TheaterFilterSheet onClose={()=>setShowFilter(false)}/>}

      {/* ── Header ── */}
      <div style={{padding:"14px 16px 0",position:"sticky",top:0,zIndex:50,background:T.navyGlass,backdropFilter:"blur(20px)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:20,color:T.white}}>Theaters</span>
          <div style={{display:"flex",gap:8}}>
            <button style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:4}}><svg viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="19" height="19"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg></button>
            <button onClick={()=>setShowFilter(true)} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:8,width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M1.5 4h12M4 7.5h7M6 11h3" stroke={T.offwhite} strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div style={{display:"flex",gap:0,borderBottom:`1px solid ${T.glassBorder}`}}>
          {[["near","Theaters Near You"],["fav","My Favorite Theaters"]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} className={tab===id?"tab-on":"tab-off"} style={{background:"none",border:"none",cursor:"pointer",padding:"10px 0",fontSize:12,fontWeight:700,letterSpacing:".02em",fontFamily:"'Outfit',sans-serif",marginRight:18,transition:"color .2s"}}>{label}</button>
          ))}
        </div>
      </div>

      {/* ── Google Maps-style map ── */}
      <div style={{position:"relative",height:180,background:"linear-gradient(135deg,#1a2a3a,#0d1e30,#091524)",overflow:"hidden",borderBottom:`1px solid ${T.glassBorder}`}}>
        {/* Grid streets — Google Maps style */}
        <svg style={{position:"absolute",inset:0,width:"100%",height:"100%"}} viewBox="0 0 360 180" preserveAspectRatio="none">
          {/* Horizontal roads */}
          <line x1="0" y1="36" x2="360" y2="36" stroke="rgba(255,255,255,.06)" strokeWidth="2"/>
          <line x1="0" y1="72" x2="360" y2="72" stroke="rgba(255,255,255,.04)" strokeWidth="1.5"/>
          <line x1="0" y1="108" x2="360" y2="108" stroke="rgba(255,255,255,.06)" strokeWidth="2"/>
          <line x1="0" y1="144" x2="360" y2="144" stroke="rgba(255,255,255,.04)" strokeWidth="1.5"/>
          {/* Vertical roads */}
          <line x1="60" y1="0" x2="60" y2="180" stroke="rgba(255,255,255,.04)" strokeWidth="1.5"/>
          <line x1="120" y1="0" x2="120" y2="180" stroke="rgba(255,255,255,.06)" strokeWidth="2"/>
          <line x1="180" y1="0" x2="180" y2="180" stroke="rgba(255,255,255,.04)" strokeWidth="1.5"/>
          <line x1="240" y1="0" x2="240" y2="180" stroke="rgba(255,255,255,.06)" strokeWidth="2"/>
          <line x1="300" y1="0" x2="300" y2="180" stroke="rgba(255,255,255,.04)" strokeWidth="1.5"/>
          {/* Diagonal / curved roads */}
          <path d="M0 160 Q90 120 180 130 Q270 140 360 100" stroke="rgba(201,168,76,.08)" strokeWidth="2.5" fill="none"/>
          <path d="M30 0 Q60 50 90 90 Q120 130 150 180" stroke="rgba(255,255,255,.035)" strokeWidth="1.5" fill="none"/>
        </svg>
        {/* Block fills — subtle neighborhood areas */}
        <div style={{position:"absolute",top:40,left:30,width:80,height:55,background:"rgba(201,168,76,.03)",borderRadius:4}}/>
        <div style={{position:"absolute",top:90,left:200,width:100,height:60,background:"rgba(147,112,219,.02)",borderRadius:4}}/>
        {/* "You" location pin */}
        <div style={{position:"absolute",left:"25%",top:"55%",transform:"translate(-50%,-100%)",display:"flex",flexDirection:"column",alignItems:"center",zIndex:10}}>
          <div style={{width:16,height:16,borderRadius:"50%",background:"#4285F4",border:"2.5px solid #fff",boxShadow:"0 2px 8px rgba(66,133,244,.5)"}}/>
          <div style={{width:24,height:24,borderRadius:"50%",background:"rgba(66,133,244,.15)",border:"1px solid rgba(66,133,244,.25)",position:"absolute",top:-4,left:"50%",transform:"translateX(-50%)",animation:"pulse 2s ease-in-out infinite"}}/>
        </div>
        {/* Theater pins */}
        {THEATERS.map((th,i)=>{
          const positions = [[55,30],[75,55],[48,75]];
          const [px,py] = positions[i] || [50,50];
          return (
            <div key={th.id} onClick={()=>{setTheater(th);setTheaterInitTab("showtimes");nav("theater-detail");}} style={{position:"absolute",left:`${px}%`,top:`${py}%`,transform:"translate(-50%,-100%)",cursor:"pointer",zIndex:10,display:"flex",flexDirection:"column",alignItems:"center"}} className="press">
              <div style={{background:T.gold,borderRadius:"50% 50% 50% 0",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",transform:"rotate(-45deg)",boxShadow:`0 3px 10px rgba(201,168,76,.4)`,border:`2px solid ${T.navy}`}}>
                <span style={{transform:"rotate(45deg)",fontSize:12,fontWeight:800,color:T.navy}}>{i+1}</span>
              </div>
              <div style={{background:"rgba(4,8,15,.85)",backdropFilter:"blur(6px)",border:`1px solid ${T.glassBorder}`,borderRadius:6,padding:"2px 6px",marginTop:3,whiteSpace:"nowrap"}}>
                <div style={{fontSize:8,fontWeight:700,color:T.white}}>{th.short||th.name.split(" ").slice(0,2).join(" ")}</div>
                <div style={{fontSize:7,color:T.gold}}>{th.distance}</div>
              </div>
            </div>
          );
        })}
        {/* Location label */}
        <div style={{position:"absolute",bottom:8,left:12,display:"flex",alignItems:"center",gap:5,background:"rgba(4,8,15,.75)",backdropFilter:"blur(6px)",borderRadius:6,padding:"4px 8px",border:`1px solid ${T.glassBorder}`}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:"#4285F4"}}/>
          <span style={{fontSize:10,color:T.offwhite,fontWeight:500}}>Los Angeles, CA</span>
        </div>
        {/* Change button */}
        <button style={{position:"absolute",bottom:8,right:12,background:"rgba(4,8,15,.75)",backdropFilter:"blur(6px)",border:`1px solid ${T.glassBorder}`,borderRadius:6,padding:"4px 10px",cursor:"pointer"}}>
          <span style={{fontSize:10,color:T.gold,fontWeight:700,fontFamily:"'Outfit',sans-serif"}}>Change</span>
        </button>
      </div>

      {/* ── Theater venue cards ── */}
      <div style={{flex:1,overflowY:"auto",padding:"10px 16px",paddingBottom:80}}>
        {(tab==="near"?THEATERS:THEATERS.filter(t=>favTheaters.includes(t.id))).map((th)=>(
          <div key={th.id} className="glass-card" style={{marginBottom:12,overflow:"hidden",cursor:"pointer"}} onClick={()=>{setTheater(th);setTheaterInitTab("showtimes");nav("theater-detail");}}>
            {/* Card header with gradient accent */}
            <div style={{padding:"14px 16px 12px",position:"relative"}}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${T.gold},rgba(201,168,76,.2))`}}/>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:T.white,lineHeight:1.25,marginBottom:4}}>{th.name}</div>
                  <div style={{fontSize:11,color:T.grey}}>{th.address} · {th.city}</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                  <button onClick={e=>toggleFav(e,th.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:17,color:favTheaters.includes(th.id)?T.gold:T.grey,lineHeight:1,padding:0,transition:"color .2s"}}>
                    {favTheaters.includes(th.id)?"★":"☆"}
                  </button>
                  <div style={{background:T.goldDim,border:`1px solid ${T.goldBorder}`,borderRadius:6,padding:"3px 8px",fontSize:11,fontWeight:700,color:T.goldLight}}>{th.distance}</div>
                </div>
              </div>
              {/* Amenity chips + screens count */}
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontSize:10,fontWeight:700,color:T.offwhite,background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:5,padding:"2px 7px"}}>{th.screens} Screens</span>
                {th.amenities.map(a=>(
                  <span key={a} style={{fontSize:9,color:T.grey,background:"rgba(255,255,255,.03)",border:`1px solid ${T.glassBorder}`,borderRadius:5,padding:"2px 7px"}}>{a}</span>
                ))}
              </div>
            </div>
            {/* Card footer actions — distinct buttons */}
            <div style={{display:"flex",gap:8,padding:"0 16px 14px"}}>
              <button className="btn-outline" style={{flex:1,padding:"9px 0",fontSize:11,fontWeight:600,borderRadius:8,fontFamily:"'Outfit',sans-serif"}} onClick={e=>{e.stopPropagation();setTheater(th);setTheaterInitTab("info");nav("theater-detail");}}>
                Details
              </button>
              <button className="btn-gold" style={{flex:1,padding:"9px 0",fontSize:11,fontWeight:700,borderRadius:8}} onClick={e=>{e.stopPropagation();setTheater(th);setTheaterInitTab("showtimes");nav("theater-detail");}}>
                Showtimes
              </button>
            </div>
          </div>
        ))}
        {tab==="fav" && favTheaters.length===0 && (
          <div style={{textAlign:"center",padding:"48px 24px",color:T.grey}}>
            <div style={{fontSize:28,marginBottom:10}}>★</div>
            <div style={{fontSize:14}}>No favorite theaters yet — tap ☆ on any theater to save it.</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// THEATER DETAIL SCREEN
// ══════════════════════════════════════════════════════
const TheaterDetailScreen = ({ theater, nav, setMovie, setShowtime, initTab }) => {
  const [tab, setTab] = useState(initTab || "showtimes");
  const [selectedDate, setSelectedDate] = useState(0);
  const [movieSheetOpen, setMovieSheetOpen] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [openPreview, setOpenPreview] = useState(null);
  if(!theater) return null;

  const dates = [
    {short:"Today", day:"", date:""},
    {short:"Tue",   day:"Tue", date:"6/3"},
    {short:"Wed",   day:"Wed", date:"6/4"},
    {short:"Thu",   day:"Thu", date:"6/5"},
    {short:"Fri",   day:"Fri", date:"6/6"},
    {short:"Sat",   day:"Sat", date:"6/7"},
  ];

  const AMENITY_GRID = [
    {icon:"🛋️", label:"Luxury Recliners"},
    {icon:"♿", label:"Wheelchair Access"},
    {icon:"🍔", label:"Dine-In Delivery"},
    {icon:"🎮", label:"Arcade Lounge"},
    {icon:"🍸", label:"Bar & Cocktails"},
    {icon:"🎧", label:"Audio Description"},
    {icon:"🚗", label:"Free Parking"},
    {icon:"👶", label:"Sensory Friendly"},
  ];

  // All-movies bottom sheet
  const MovieSheet = () => (
    <div style={{position:"absolute",inset:0,zIndex:300,display:"flex",flexDirection:"column"}}>
      <div onClick={()=>setMovieSheetOpen(false)} style={{flex:1,background:"rgba(0,0,0,.55)"}}/>
      <div style={{background:T.navyMid,borderRadius:"20px 20px 0 0",maxHeight:"80%",display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${T.glassBorder}`}}>
          <span style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700}}>Movies</span>
          <button onClick={()=>setMovieSheetOpen(false)} style={{background:"none",border:"none",color:T.grey,fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{overflowY:"auto"}}>
          {["All Movies",...MOVIES.map(m=>m.title),...COMING_SOON.map(m=>m.title)].map((title,i)=>(
            <div key={title} onClick={()=>setMovieSheetOpen(false)} style={{padding:"14px 20px",borderBottom:`1px solid ${T.glassBorder}`,cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
              {i===0
                ? <span style={{fontSize:11,color:T.gold,fontWeight:700,letterSpacing:".06em"}}>✓</span>
                : <span style={{fontSize:11,color:"transparent"}}>✓</span>
              }
              <span style={{fontSize:14,color:i===0?T.gold:T.white,fontWeight:i===0?700:400}}>{title}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",position:"relative"}}>
      {movieSheetOpen && <MovieSheet/>}

      {/* ── Sticky header ── */}
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:10,position:"sticky",top:0,zIndex:50,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
        <button onClick={()=>nav("theaters")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0}}>←</button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:14,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{theater.name}</div>
          <div style={{fontSize:10,color:T.grey,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{theater.address} · {theater.city}</div>
        </div>
        <button style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.gold,flexShrink:0}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
        </button>
      </div>

      {/* ── Change Theatre + contact row ── */}
      <div style={{background:T.navyCard,borderBottom:`1px solid ${T.glassBorder}`,flexShrink:0}}>
        <div style={{padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <button onClick={()=>setInfoExpanded(!infoExpanded)} style={{background:"none",border:"none",cursor:"pointer",color:T.gold,fontSize:11,fontWeight:700,fontFamily:"'Outfit',sans-serif",display:"flex",alignItems:"center",gap:4}}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke={T.gold} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Change Theater
            </button>
          </div>
          <div style={{display:"flex",gap:14}}>
            <button style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              <span style={{fontSize:11,color:T.grey}}>{theater.phone}</span>
            </button>
          </div>
        </div>

        {/* Mini inline map */}
        <div style={{position:"relative",height:120,background:"linear-gradient(180deg,#0d1e30,#091524)",overflow:"hidden"}}>
          {[[8,50,92,50],[20,20,20,80],[50,15,50,85],[75,25,75,75],[35,30,90,30],[30,70,88,70]].map(([x1,y1,x2,y2],i)=>(
            <svg key={i} style={{position:"absolute",inset:0,width:"100%",height:"100%"}} viewBox="0 0 100 100" preserveAspectRatio="none">
              <line x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`} stroke="rgba(255,255,255,0.055)" strokeWidth="1.5"/>
            </svg>
          ))}
          {/* Theater pin */}
          <div style={{position:"absolute",left:"50%",top:"50%",transform:"translate(-50%,-100%)",display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div style={{width:26,height:26,borderRadius:"50%",background:T.gold,border:`2px solid ${T.navy}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13}}>📍</div>
            <div style={{width:0,height:0,borderLeft:"6px solid transparent",borderRight:"6px solid transparent",borderTop:`9px solid ${T.gold}`,marginTop:-1}}/>
          </div>
          <div style={{position:"absolute",bottom:6,right:8,fontSize:9,color:T.grey,opacity:.6}}>Los Angeles, CA</div>
        </div>
      </div>

      {/* ── Pill tab switcher (mirrors Movie Detail) ── */}
      <div style={{padding:"12px 16px",background:T.navy,flexShrink:0}}>
        <div style={{display:"flex",background:T.navyLight,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:3,gap:2}}>
          {["Showtimes","Features","Info"].map(t=>{
            const key=t.toLowerCase();
            const on=tab===key;
            return (
              <button key={t} onClick={()=>setTab(key)} style={{flex:1,padding:"8px 0",borderRadius:9,background:on?T.gold:"transparent",color:on?T.navy:T.grey,border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:700,fontSize:12,letterSpacing:".03em",transition:"all .2s"}}>{t}</button>
            );
          })}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div style={{flex:1,overflowY:"auto",paddingBottom:80}}>

        {/* ── SHOWTIMES ── */}
        {tab==="showtimes" && (
          <div className="fade">
            {/* Date scroller — matches MovieDetailScreen style */}
            <div className="hscroll" style={{padding:"0 16px",marginBottom:12,gap:7}}>
              {dates.map((d,i)=>(
                <button key={i} onClick={()=>setSelectedDate(i)} style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",padding:"9px 13px",borderRadius:12,background:selectedDate===i?T.goldDim:"transparent",color:selectedDate===i?T.goldLight:T.grey,border:`1.5px solid ${selectedDate===i?T.goldBorder:T.greyDim}`,cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all .18s"}}>
                  <span style={{fontSize:9,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",marginBottom:2}}>{d.short==="Today"?"TODAY":d.day.toUpperCase()}</span>
                  <span style={{fontSize:14,fontWeight:700}}>{d.short==="Today"?"Now":d.date}</span>
                </button>
              ))}
            </div>

            {/* Filter row: Movie + Format */}
            <div style={{display:"flex",gap:8,padding:"0 16px",marginBottom:14}}>
              <button onClick={()=>setMovieSheetOpen(true)} style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"8px 0",borderRadius:10,background:T.navyLight,border:`1px solid ${T.glassBorder}`,cursor:"pointer"}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 4v16M16 4v16M2 9h4M18 9h4"/></svg>
                <span style={{fontSize:11,fontWeight:600,color:T.offwhite,fontFamily:"'Outfit',sans-serif"}}>All Movies</span>
                <span style={{fontSize:10,color:T.grey}}>▾</span>
              </button>
            </div>

            {/* Movie showtime cards — full detail matching movie detail screen */}
            <div style={{padding:"0 16px"}}>
              {MOVIES.map(m=>{
                const thData = SHOWTIMES_DATA[theater.id] || { formats:[{ fmt:"Standard", tagline:"", amenities:"Recliner Seats · Reserved Seating", times:["2:00 PM","5:15 PM","8:30 PM"] }] };
                const dateLabel = dates[selectedDate].short==="Today"?"Today":`${dates[selectedDate].day} ${dates[selectedDate].date}`;

                const handleTheaterTime = (fmt, t) => {
                  const base = FORMAT_PRICING[fmt.fmt] || 16.50;
                  const mat = isMatinee(t);
                  const price = mat ? base - MATINEE_DISCOUNT : base;
                  setMovie(m);
                  setShowtime({ movie:m, theater, time:t, fmt:fmt.fmt, date:dateLabel, basePrice:price, isMatinee:mat, showroom:fmt.showroom, seatsLeft:fmt.seatsLeft });
                  nav("seat-map");
                };

                return (
                  <div key={m.id} className="glass-card" style={{marginBottom:14,overflow:"hidden"}}>
                    {/* Movie header */}
                    <div style={{padding:"14px 16px 10px",position:"relative"}}>
                      <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${T.gold},rgba(201,168,76,.2))`}} role="presentation"/>
                      <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                        <div onClick={()=>{setMovie(m);nav("movie-detail");}} style={{width:44,height:62,borderRadius:7,overflow:"hidden",flexShrink:0,border:`1px solid ${T.glassBorder}`,cursor:"pointer"}} role="button" tabIndex={0} aria-label={`View details for ${m.title}`}>
                          <MoviePoster movieId={m.id} width={44} height={62} mini/>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:T.white,lineHeight:1.25,marginBottom:3}}>{m.title}</div>
                          <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                            <span style={{fontSize:9,fontWeight:700,color:T.grey,background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:3,padding:"1px 5px"}}>{m.rating}</span>
                            <span style={{fontSize:10,color:T.grey}}>{m.genre}</span>
                            <span style={{fontSize:10,color:T.greyDim}}>·</span>
                            <span style={{fontSize:10,color:T.grey}}>{m.runtime}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Format sections */}
                    {thData.formats.map((fmt,fi) => {
                      const previewKey=`${m.id}-${theater.id}-${fi}`;
                      return (
                      <div key={fi} style={{borderTop:`1px solid ${T.glassBorder}`,padding:"12px 16px"}}>
                        {/* Format badge + from-price */}
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{background:`linear-gradient(135deg,${T.goldDim},rgba(201,168,76,.05))`,border:`1px solid ${T.goldBorder}`,borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:800,color:T.goldLight,letterSpacing:".04em"}}>{fmt.fmt}</span>
                            {fmt.tagline&&<span style={{fontSize:9,color:T.grey,fontStyle:"italic"}}>{fmt.tagline}</span>}
                          </div>
                          <span style={{fontSize:9,color:T.offwhite,fontWeight:600}}>from <span style={{color:T.white,fontWeight:700}}>${((FORMAT_PRICING[fmt.fmt]||16.50) - MATINEE_DISCOUNT + SERVICE_FEE_GLOBAL).toFixed(2)}</span></span>
                        </div>
                        <div style={{fontSize:9,color:T.grey,marginBottom:3,lineHeight:1.4}}>{fmt.amenities}</div>
                        {fmt.showroom&&SHOWROOMS[fmt.showroom]&&(
                          <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:8}}>
                            <span style={{fontSize:8,color:T.offwhite,fontWeight:600}}>{SHOWROOMS[fmt.showroom].name}</span>
                            <span style={{fontSize:7,color:T.grey}}>· {SHOWROOMS[fmt.showroom].seats} seats · {SHOWROOMS[fmt.showroom].tag}</span>
                          </div>
                        )}

                        {/* Time pills with all-in pricing */}
                        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                          {fmt.times.map(t=>{
                            const base = FORMAT_PRICING[fmt.fmt] || 16.50;
                            const mat = isMatinee(t);
                            const allIn = (mat ? base - MATINEE_DISCOUNT : base) + SERVICE_FEE_GLOBAL;
                            const left = fmt.seatsLeft?.[t];
                            const lowSeats = left && left <= 20;
                            return (
                              <button key={t} className="pill-time press" onClick={()=>handleTheaterTime(fmt,t)}
                                style={{fontSize:11,padding:"6px 10px",display:"flex",flexDirection:"column",alignItems:"center",gap:2,position:"relative"}}
                                aria-label={`${t} showtime, $${allIn.toFixed(2)}${mat?" matinee pricing":""}${lowSeats?`, only ${left} seats left`:""}`}>
                                <span>{t}</span>
                                <span style={{fontSize:8,color:mat?T.green:T.grey,fontWeight:600}}>
                                  ${allIn.toFixed(2)}{mat&&" ✦"}
                                </span>
                                {mat && <span style={{position:"absolute",top:-6,right:-4,background:T.green,color:"#fff",fontSize:6,fontWeight:800,borderRadius:3,padding:"1px 3px",letterSpacing:".03em"}}>MATINEE</span>}
                                {lowSeats && <span style={{fontSize:7,color:T.red,fontWeight:700}}>{left<=10?`${left} left!`:`${left} left`}</span>}
                              </button>
                            );
                          })}
                        </div>

                        {/* Fee transparency */}
                        <div style={{fontSize:8,color:T.grey,display:"flex",alignItems:"center",gap:3,marginBottom:8}}>
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                          Prices include ${SERVICE_FEE_GLOBAL.toFixed(2)} fee · No hidden charges
                        </div>

                        {/* Check Seats */}
                        <button onClick={()=>setOpenPreview(openPreview===previewKey?null:previewKey)} aria-expanded={openPreview===previewKey} aria-label="Toggle seat availability preview" style={{display:"inline-flex",alignItems:"center",gap:6,background:openPreview===previewKey?T.goldDim:"rgba(255,255,255,.03)",border:`1px solid ${openPreview===previewKey?T.goldBorder:T.greyDim}`,borderRadius:8,padding:"6px 12px",cursor:"pointer",transition:"all .18s"}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={openPreview===previewKey?T.goldLight:T.grey} strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          <span style={{fontSize:11,color:openPreview===previewKey?T.goldLight:T.grey,fontWeight:600,fontFamily:"'Outfit',sans-serif"}}>Check Seats</span>
                          <span style={{fontSize:9,color:T.grey,transform:openPreview===previewKey?"rotate(180deg)":"none",display:"inline-block",transition:"transform .2s"}}>▾</span>
                        </button>
                        {openPreview===previewKey&&<InlineSeatPreview showtimeKey={`${theater.id}-${m.id}-${fmt.fmt}`} times={fmt.times} seatsLeft={fmt.seatsLeft}/>}
                      </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── FEATURES ── */}
        {tab==="features" && (
          <div className="fade" style={{padding:"16px"}}>
            {/* Featured Movies carousel */}
            <div style={{marginBottom:22}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <div style={{width:3,height:16,borderRadius:2,background:T.gold}}/>
                <span style={{fontSize:11,fontWeight:700,color:T.offwhite,letterSpacing:".08em",textTransform:"uppercase"}}>Featured Movies</span>
              </div>
              <div className="hscroll" style={{gap:12}}>
                {MOVIES.slice(0,5).map(m=>(
                  <div key={m.id} className="press card-lift" onClick={()=>{setMovie(m);nav("movie-detail");}} style={{flexShrink:0,width:130,borderRadius:12,overflow:"hidden",border:`1px solid ${T.glassBorder}`,background:T.navyCard}}>
                    <div style={{height:110,position:"relative",overflow:"hidden"}}>
                      <MoviePoster movieId={m.id} width={130} height={110} mini/>
                      {/* Play overlay */}
                      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.25)"}}>
                        <div style={{width:32,height:32,borderRadius:"50%",border:`2px solid ${T.gold}`,background:"rgba(4,8,26,.5)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                          <div style={{width:0,height:0,borderTop:"7px solid transparent",borderBottom:"7px solid transparent",borderLeft:`11px solid ${T.gold}`,marginLeft:2}}/>
                        </div>
                      </div>
                      {/* Get Tickets badge */}
                      <div style={{position:"absolute",bottom:0,left:0,right:0,background:`linear-gradient(to top,${T.gold},transparent)`,padding:"18px 8px 6px",textAlign:"center"}}>
                        <span style={{fontSize:9,fontWeight:800,color:T.navy,letterSpacing:".06em"}}>GET TICKETS</span>
                      </div>
                    </div>
                    <div style={{padding:"8px 10px 10px"}}>
                      <div style={{fontSize:11,fontWeight:600,color:T.white,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.title}</div>
                      <div style={{fontSize:10,color:T.grey,marginTop:2}}>{m.rating} · {m.runtime}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Theater Amenities grid */}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <div style={{width:3,height:16,borderRadius:2,background:T.gold}}/>
                <span style={{fontSize:11,fontWeight:700,color:T.offwhite,letterSpacing:".08em",textTransform:"uppercase"}}>Theater Amenities</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {AMENITY_GRID.slice(0, theater.amenities.length + 2 > 8 ? 8 : theater.amenities.length + 2).map(({icon,label})=>(
                  <div key={label} style={{background:T.navyLight,border:`1px solid ${T.glassBorder}`,borderRadius:12,padding:"14px 12px",display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:38,height:38,borderRadius:10,background:T.goldDim,border:`1px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{icon}</div>
                    <span style={{fontSize:12,fontWeight:600,color:T.white,lineHeight:1.3}}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── INFO ── */}
        {tab==="info" && (
          <div className="fade" style={{padding:"16px"}}>
            <div className="glass-card" style={{padding:16,marginBottom:14}}>
              {[
                ["Address", theater.address+", "+theater.city],
                ["Phone", theater.phone],
                ["Screens", `${theater.screens} auditoriums`],
                ["Hours", "Mon–Sun  10:00 AM – 12:00 AM"],
                ["Parking", "Free validated parking"],
                ["Accessibility", "Wheelchair access · Audio description · Open captions"],
              ].map(([k,v])=>(
                <div key={k} style={{padding:"10px 0",borderBottom:`1px solid ${T.glassBorder}`}}>
                  <div style={{fontSize:10,color:T.grey,marginBottom:3,letterSpacing:".06em",textTransform:"uppercase"}}>{k}</div>
                  <div style={{fontSize:13,color:T.white,lineHeight:1.5}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:10}}>
              <button className="btn-outline" style={{flex:1,padding:"11px 0",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                Call
              </button>
              <button className="btn-outline" style={{flex:1,padding:"11px 0",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                Directions
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MY REALVIEW
// ══════════════════════════════════════════════════════
const MyReelViewScreen = ({ nav, setShowtime, setSeatData }) => {
  const [tab,setTab] = useState("rewards");
  return (
    <div className="screen" style={{paddingBottom:80}}>
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:18}}>My ReelView</span>
        <button style={{background:"none",border:"none",cursor:"pointer",color:T.grey,fontSize:13,fontFamily:"'Outfit',sans-serif",fontWeight:600}}>Settings</button>
      </div>
      {/* Profile */}
      <div style={{padding:"18px 16px 14px",background:`linear-gradient(180deg,${T.navyLight} 0%,${T.navy} 100%)`,marginBottom:2,display:"flex",gap:14,alignItems:"center"}}>
        <div style={{width:58,height:58,borderRadius:"50%",background:`linear-gradient(135deg,${T.goldDim},${T.navyLight})`,border:`2px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>🎬</div>
        <div>
          <div style={{fontSize:17,fontWeight:700,color:T.white}}>Alex Rivera</div>
          <div style={{fontSize:12,color:T.grey}}>Member since Jan 2023</div>
          <span className="tag" style={{marginTop:5,display:"inline-block"}}>⭐ Gold Member</span>
        </div>
      </div>
      {/* Equally spaced tabs */}
      <div style={{display:"flex",borderBottom:`1px solid ${T.glassBorder}`,marginBottom:14}}>
        {[["rewards","Rewards"],["tickets","Tickets"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} className={tab===id?"tab-on":"tab-off"}
            style={{flex:1,background:"none",border:"none",cursor:"pointer",padding:"12px 0",fontSize:13,fontWeight:600,fontFamily:"'Outfit',sans-serif",textAlign:"center"}}>
            {label}
          </button>
        ))}
      </div>

      <div style={{padding:"0 16px"}}>
        {tab==="rewards"&&(
          <div className="fade">
            {/* Points balance card */}
            <div style={{background:`linear-gradient(135deg,#1a1200,${T.navyLight})`,border:`1px solid ${T.goldBorder}`,borderRadius:18,padding:20,marginBottom:18,position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:-28,right:-28,width:110,height:110,borderRadius:"50%",background:T.goldDim}}/>
              <div style={{fontSize:10,color:T.goldLight,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Reward Points</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:44,fontWeight:900,color:T.gold,marginBottom:3}}>5,240</div>
              <div style={{fontSize:12,color:T.grey,marginBottom:4}}>= <span style={{color:T.white,fontWeight:700}}>$5.00</span> available reward value</div>
              <div style={{fontSize:10,color:T.grey,marginBottom:12}}>4,760 pts to next $10.00 reward</div>
              <div style={{height:6,borderRadius:3,background:T.navyMid,overflow:"hidden"}}>
                <div style={{width:"52.4%",height:"100%",borderRadius:3,background:`linear-gradient(90deg,${T.gold},${T.goldLight})`}}/>
              </div>
              <div style={{fontSize:9,color:T.grey,marginTop:6}}>5,000 pts = $5 · Points apply at checkout automatically</div>
            </div>
            <div style={{fontSize:11,fontWeight:700,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:10}}>Points History</div>
            {[{l:"Movie Ticket — The Bride!",p:"+250",d:"Mar 7"},{l:"Concessions Purchase",p:"+85",d:"Mar 7"},{l:"Bonus: Double Points Weekend",p:"+400",d:"Feb 28"},{l:"Reward Applied — $5.00 off",p:"−5,000",d:"Feb 20"},{l:"Movie Ticket — Wuthering Heights",p:"+250",d:"Feb 22"}].map((item,i)=>(
              <div key={i} className="glass-card" style={{padding:12,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div><div style={{fontSize:13,color:T.white,fontWeight:500}}>{item.l}</div><div style={{fontSize:10,color:T.grey,marginTop:2}}>{item.d}</div></div>
                <div style={{fontSize:14,fontWeight:700,color:item.p.startsWith("+")?T.gold:T.red}}>{item.p}</div>
              </div>
            ))}
          </div>
        )}
        {tab==="tickets"&&(
          <div className="fade">
            {[
              {title:"Peaky Blinders: The Immortal Man",date:"Mar 15, 2026",time:"7:30 PM",theater:"ReelView Grand Cineplex",theaterShort:"Grand Cineplex",seats:["E5","E6"],fmt:"IMAX",movie:MOVIES[3],theaterObj:THEATERS[0],upcoming:true},
              {title:"Hoppers",date:"Mar 22, 2026",time:"2:00 PM",theater:"ReelView Marina Bay",theaterShort:"Marina Bay",seats:["F4","F5","F6"],fmt:"Standard",movie:MOVIES[1],theaterObj:THEATERS[2],upcoming:true},
              {title:"Wuthering Heights",date:"Feb 22, 2026",time:"8:00 PM",theater:"ReelView Westside 10",theaterShort:"Westside 10",seats:["G7","G8"],fmt:"Dolby Atmos",movie:MOVIES[0],theaterObj:THEATERS[1],upcoming:false},
            ].map((t,i)=>(
              <div key={i} className="glass-card" style={{padding:14,marginBottom:10,borderLeft:`3px solid ${t.upcoming?T.gold:T.greyDim}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:14,fontWeight:700,color:T.white,flex:1,paddingRight:8}}>{t.title}</div>
                  <span className="tag" style={{background:t.upcoming?T.goldDim:"rgba(255,255,255,.04)",color:t.upcoming?T.goldLight:T.grey,flexShrink:0}}>{t.upcoming?"Upcoming":"Past"}</span>
                </div>
                <div style={{fontSize:11,color:T.grey,marginBottom:1}}>{t.theater}</div>
                <div style={{fontSize:11,color:T.grey,marginBottom:t.upcoming?12:0}}>{t.date} · {t.time} · Seats: {t.seats.join(", ")}</div>
                {t.upcoming&&(
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {/* Row 1: View Ticket + Add Concessions */}
                    <div style={{display:"flex",gap:8}}>
                      <button className="btn-outline" style={{flex:1,padding:"9px 6px",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",gap:5}}
                        onClick={()=>{
                          setShowtime({movie:t.movie,theater:t.theaterObj,fmt:t.fmt,time:t.time,date:t.date,basePrice:FORMAT_PRICING[t.fmt]||16.50});
                          setSeatData(t.seats);
                          nav("ticket-confirm");
                        }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3l-4 4-4-4"/></svg>
                        View Ticket
                      </button>
                      <button className="btn-outline" style={{flex:1,padding:"9px 6px",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",gap:5}} onClick={()=>nav("concessions")}>
                        🍿 Add Concessions
                      </button>
                    </div>
                    {/* Row 2: Invite Friend — opens ticket-confirm where modal lives */}
                    <button className="btn-gold" style={{width:"100%",padding:"10px 0",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:7}}
                      onClick={()=>{
                        setShowtime({movie:t.movie,theater:t.theaterObj,fmt:t.fmt,time:t.time,date:t.date,basePrice:FORMAT_PRICING[t.fmt]||16.50});
                        setSeatData(t.seats);
                        nav("ticket-confirm");
                      }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                      Invite a Friend
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// CONCESSIONS
// ══════════════════════════════════════════════════════
const ConcessionsScreen = ({ nav, setConcessionOrder }) => {
  const [cart,setCart] = useState({});
  const [tab,setTab] = useState("All");
  const [favIds,setFavIds] = useState([1,7]); // user's favorite items
  const [pickupMovie,setPickupMovie] = useState(0); // index into upcoming movies, -1 = no movie
  const [pickupTime,setPickupTime] = useState(0); // index into pickup slots
  const [pickupDate,setPickupDate] = useState(0); // date index for no-movie

  const PICKUP_MOVIES = [
    {title:"Peaky Blinders: The Immortal Man",time:"7:30 PM",theater:"ReelView Grand"},
    {title:"Hoppers",time:"2:00 PM",theater:"ReelView Marina Bay"},
  ];
  const PICKUP_DATES = ["Today","Mon 3/10","Tue 3/11","Wed 3/12","Thu 3/13","Fri 3/14"];
  const PICKUP_SLOTS_MOVIE = ["At showtime","15 min before","30 min before","Custom time"];
  const PICKUP_SLOTS_NO_MOVIE = ["12:00 PM","2:00 PM","4:00 PM","6:00 PM","8:00 PM"];
  const PICKUP_SLOTS = pickupMovie===-1 ? PICKUP_SLOTS_NO_MOVIE : PICKUP_SLOTS_MOVIE;
  const RECENT_IDS = [1,2,7]; // recently purchased item IDs

  const cats = ["All","Snacks","Drinks","Combos","★ Favorites","Recent"];
  const toggleFav = id => setFavIds(f => f.includes(id) ? f.filter(x=>x!==id) : [...f,id]);

  const filtered = tab==="★ Favorites"
    ? CONCESSIONS.filter(c=>favIds.includes(c.id))
    : tab==="Recent"
      ? CONCESSIONS.filter(c=>RECENT_IDS.includes(c.id))
      : tab==="All" ? CONCESSIONS : CONCESSIONS.filter(c=>c.cat===tab);

  const totalAmt = Object.entries(cart).reduce((s,[id,q])=>{const it=CONCESSIONS.find(c=>c.id===+id);return s+(it?it.price*q:0);},0);
  const totalItems = Object.values(cart).reduce((a,b)=>a+b,0);
  const add=id=>setCart(c=>({...c,[id]:(c[id]||0)+1}));
  const rem=id=>setCart(c=>{const n={...c};if(n[id]>1)n[id]--;else delete n[id];return n;});

  return (
    <div className="screen" style={{display:"flex",flexDirection:"column",paddingBottom:0}}>
      {/* Header */}
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:18}}>Concessions</span>
        <span style={{fontSize:10,color:T.grey}}>Pre-order & skip the line</span>
      </div>

      {/* Movie / Pickup time selector */}
      <div style={{flexShrink:0,background:T.navyCard,borderBottom:`1px solid ${T.glassBorder}`,padding:"10px 16px"}}>
        <div style={{fontSize:9,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:6}}>ORDERING FOR</div>
        {/* Movie selector */}
        <div style={{display:"flex",gap:6,marginBottom:8,overflowX:"auto"}}>
          {PICKUP_MOVIES.map((m,i)=>(
            <button key={i} onClick={()=>{setPickupMovie(i);setPickupTime(0);}}
              style={{flexShrink:0,padding:"8px 12px",borderRadius:10,
                background:pickupMovie===i?T.goldDim:"transparent",
                border:`1.5px solid ${pickupMovie===i?T.goldBorder:T.greyDim}`,
                cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all .18s",textAlign:"left"}}>
              <div style={{fontSize:11,fontWeight:700,color:pickupMovie===i?T.goldLight:T.white}}>{m.title}</div>
              <div style={{fontSize:9,color:pickupMovie===i?T.goldLight:T.grey,marginTop:1}}>{m.time} · {m.theater}</div>
            </button>
          ))}
          <button onClick={()=>{setPickupMovie(-1);setPickupTime(0);}}
            style={{flexShrink:0,padding:"8px 12px",borderRadius:10,
              background:pickupMovie===-1?T.goldDim:"transparent",
              border:`1.5px ${pickupMovie===-1?"solid":"dashed"} ${pickupMovie===-1?T.goldBorder:T.greyDim}`,
              cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontFamily:"'Outfit',sans-serif",transition:"all .18s"}}>
            <span style={{fontSize:14,color:pickupMovie===-1?T.goldLight:T.grey}}>🍿</span>
            <div style={{textAlign:"left"}}>
              <div style={{fontSize:11,fontWeight:700,color:pickupMovie===-1?T.goldLight:T.grey}}>No Movie</div>
              <div style={{fontSize:9,color:pickupMovie===-1?T.goldLight:T.grey,marginTop:1}}>Just concessions</div>
            </div>
          </button>
        </div>
        {/* Date picker — shown for No Movie */}
        {pickupMovie===-1&&(
          <div style={{display:"flex",gap:6,overflowX:"auto",marginBottom:8}}>
            {PICKUP_DATES.map((d,i)=>(
              <button key={d} onClick={()=>setPickupDate(i)}
                style={{flexShrink:0,padding:"5px 10px",borderRadius:20,fontSize:10,fontWeight:600,
                  background:pickupDate===i?T.goldDim:"transparent",
                  border:`1px solid ${pickupDate===i?T.goldBorder:T.greyDim}`,
                  color:pickupDate===i?T.goldLight:T.grey,
                  cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all .18s"}}>
                {d}
              </button>
            ))}
          </div>
        )}
        {/* Pickup time */}
        <div style={{display:"flex",gap:6,overflowX:"auto"}}>
          {PICKUP_SLOTS.map((s,i)=>(
            <button key={s} onClick={()=>setPickupTime(i)}
              style={{flexShrink:0,padding:"5px 10px",borderRadius:20,fontSize:10,fontWeight:600,
                background:pickupTime===i?T.goldDim:"transparent",
                border:`1px solid ${pickupTime===i?T.goldBorder:T.greyDim}`,
                color:pickupTime===i?T.goldLight:T.grey,
                cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all .18s"}}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Category tabs — horizontally scrollable */}
      <div style={{flexShrink:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        <div style={{display:"flex",padding:"0 16px",gap:16,borderBottom:`1px solid ${T.glassBorder}`,whiteSpace:"nowrap"}}>
          {cats.map(c=>(
            <button key={c} onClick={()=>setTab(c)} className={tab===c?"tab-on":"tab-off"}
              style={{background:"none",border:"none",cursor:"pointer",padding:"10px 0",fontSize:12,fontWeight:600,fontFamily:"'Outfit',sans-serif",flexShrink:0}}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state for favorites/recent */}
      {filtered.length===0 && (
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>{tab==="★ Favorites"?"⭐":"🕐"}</div>
            <div style={{fontSize:13,color:T.grey}}>{tab==="★ Favorites"?"Tap the ★ on any item to add favorites":"No recent purchases yet"}</div>
          </div>
        </div>
      )}

      {/* Item grid */}
      {filtered.length>0 && (
        <div style={{flex:1,overflowY:"auto",padding:"14px 16px",paddingBottom:20}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {filtered.map(item=>(
              <div key={item.id} className="glass-card" style={{padding:14,position:"relative"}}>
                {/* Favorite star */}
                <button onClick={()=>toggleFav(item.id)}
                  style={{position:"absolute",top:8,right:8,background:"none",border:"none",cursor:"pointer",fontSize:14,color:favIds.includes(item.id)?T.gold:T.greyDim,transition:"color .2s",padding:0,lineHeight:1}}>
                  {favIds.includes(item.id)?"★":"☆"}
                </button>
                <div style={{width:64,height:64,borderRadius:10,overflow:"hidden",margin:"0 auto 8px",background:T.navyLight}}>
                  {CONCESSION_IMAGES[item.id] ? <img src={CONCESSION_IMAGES[item.id]} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}} loading="lazy"/> : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:30}}>{item.emoji}</div>}
                </div>
                <div style={{fontSize:12,fontWeight:600,color:T.white,marginBottom:2,textAlign:"center"}}>{item.name}</div>
                <div style={{fontSize:13,color:T.gold,fontWeight:700,textAlign:"center",marginBottom:10}}>${item.price.toFixed(2)}</div>
                {cart[item.id]?(
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <button onClick={()=>rem(item.id)} style={{width:28,height:28,borderRadius:"50%",background:T.navyLight,border:`1px solid ${T.greyDim}`,cursor:"pointer",color:T.white,fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                    <span style={{fontWeight:700,color:T.white}}>{cart[item.id]}</span>
                    <button onClick={()=>add(item.id)} style={{width:28,height:28,borderRadius:"50%",background:T.goldDim,border:`1px solid ${T.goldBorder}`,cursor:"pointer",color:T.goldLight,fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                  </div>
                ):(
                  <button className="btn-outline" style={{width:"100%",padding:8,fontSize:12}} onClick={()=>add(item.id)}>Add</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cart footer — flush above bottom nav */}
      {totalItems>0&&(
        <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"10px 16px 12px",animation:"slideUp .25s ease"}}>
          <button className="btn-gold" style={{width:"100%",padding:"13px 20px",fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center"}}
            onClick={()=>{
              const cartItems = Object.entries(cart).map(([id,qty])=>{const it=CONCESSIONS.find(c=>c.id===+id);return it?{...it,qty}:null;}).filter(Boolean);
              setConcessionOrder({
                items:cartItems,
                pickupMovie:pickupMovie===-1?{title:"No Movie",time:"",theater:"ReelView Grand Cineplex"}:PICKUP_MOVIES[pickupMovie],
                pickupTime:PICKUP_SLOTS[pickupTime],
                totalAmt,totalItems
              });
              nav("concessions-checkout");
            }}>
            <span>🛒 {totalItems} item{totalItems>1?"s":""}</span>
            <span>Checkout · ${totalAmt.toFixed(2)}</span>
          </button>
        </div>
      )}
    </div>
  );
};

// CONCESSIONS CHECKOUT
const ConcessionsCheckoutScreen = ({ nav, order }) => {
  const [pay, setPay] = useState("apple");
  const [promoOpen, setPromoOpen] = useState(false);
  const [promo, setPromo] = useState("");
  const [promoOK, setPromoOK] = useState(false);

  const items = order?.items || [];
  const sub = order?.totalAmt || 0;
  const disc = promoOK ? sub * 0.10 : 0;
  const total = sub - disc;

  const PAYMENT_OPTS = [
    {id:"apple", label:"Apple Pay", icon:"🍎"},
    {id:"gpay",  label:"Google Pay", icon:"G"},
    {id:"paypal",label:"PayPal", icon:"P"},
    {id:"venmo", label:"Venmo", icon:"V"},
    {id:"card",  label:"Credit / Debit Card", icon:"💳"},
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
      {/* Header */}
      <div style={{flexShrink:0,background:T.navyGlass,backdropFilter:"blur(20px)",borderBottom:`1px solid ${T.glassBorder}`}}>
        <div style={{padding:"12px 16px",display:"flex",alignItems:"flex-start",gap:10}}>
          <button onClick={()=>nav("concessions")} style={{background:T.navyLight,border:`1px solid ${T.greyDim}`,borderRadius:10,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.white,fontSize:15,flexShrink:0,marginTop:2}}>←</button>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:16,color:T.white}}>Concessions Checkout</div>
            <div style={{fontSize:11,color:T.grey,marginTop:2}}>{order?.pickupMovie?.title} · {order?.pickupMovie?.time}</div>
          </div>
          <button onClick={()=>nav("concessions")} style={{background:"none",border:"none",color:T.grey,fontSize:20,cursor:"pointer",flexShrink:0}}>×</button>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{flex:1,overflowY:"auto"}}>
        <div style={{padding:"14px 16px"}}>

          {/* Pickup info card */}
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:14,marginBottom:14,display:"flex",gap:12,alignItems:"center"}}>
            <div style={{width:40,height:40,borderRadius:10,background:T.goldDim,border:`1px solid ${T.goldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>🍿</div>
            <div style={{flex:1}}>
              <div style={{fontSize:12,fontWeight:700,color:T.white}}>Pickup: {order?.pickupTime || "At showtime"}</div>
              <div style={{fontSize:10,color:T.grey,marginTop:1}}>{order?.pickupMovie?.theater || "ReelView Grand"} · Concessions Counter</div>
            </div>
          </div>

          {/* Order items */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Your Order ({order?.totalItems || 0} items)</div>
            {items.map((item,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${T.glassBorder}`}}>
                <div style={{width:36,height:36,borderRadius:8,overflow:"hidden",flexShrink:0,background:T.navyLight}}>
                  {CONCESSION_IMAGES[item.id] ? <img src={CONCESSION_IMAGES[item.id]} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}} loading="lazy"/> : <span style={{fontSize:22,display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:"100%"}}>{item.emoji}</span>}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:T.white,fontWeight:500}}>{item.name}</div>
                  <div style={{fontSize:10,color:T.grey}}>Qty: {item.qty}</div>
                </div>
                <span style={{fontSize:13,color:T.white,fontWeight:600}}>${(item.price*item.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Promo — grouped compact card matching Ticket Checkout */}
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
            <div style={{padding:"0 14px"}}>
              <button onClick={()=>setPromoOpen(!promoOpen)} style={{width:"100%",background:"none",border:"none",cursor:"pointer",padding:"10px 0",display:"flex",alignItems:"center",justifyContent:"space-between",fontFamily:"'Outfit',sans-serif"}}>
                <span style={{fontSize:12,color:T.offwhite,fontWeight:500}}>Promo Code or Voucher</span>
                <span style={{color:T.grey,fontSize:12,transform:promoOpen?"rotate(180deg)":"none",transition:"transform .2s"}}>▾</span>
              </button>
              {promoOpen&&(
                <div style={{paddingBottom:12,display:"flex",gap:8}}>
                  <input className="inp" value={promo} onChange={e=>setPromo(e.target.value)} placeholder="Enter code" style={{flex:1,padding:"9px 12px",fontSize:12}}/>
                  <button className="btn-outline" style={{padding:"9px 12px",fontSize:11,whiteSpace:"nowrap"}} onClick={()=>promo.length>2&&setPromoOK(true)}>Apply</button>
                </div>
              )}
            </div>
            {promoOK&&<div style={{padding:"8px 14px",fontSize:10,color:T.green,fontWeight:600,background:"rgba(39,174,96,.06)",borderTop:`1px solid ${T.glassBorder}`}}>✓ 10% promo discount applied</div>}
          </div>

          {/* Payment — selectable icon cards matching Ticket Checkout */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Payment Method</div>
            {/* Quick-pay row */}
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              {PAYMENT_OPTS.filter(o=>o.id!=="card").map(opt=>(
                <button key={opt.id} onClick={()=>setPay(opt.id)} style={{flex:1,padding:"10px 4px",borderRadius:10,background:pay===opt.id?T.goldDim:T.navyCard,border:`1.5px solid ${pay===opt.id?T.goldBorder:T.glassBorder}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,transition:"all .15s"}}>
                  <span style={{fontSize:16}}>{opt.icon}</span>
                  <span style={{fontSize:8,fontWeight:600,color:pay===opt.id?T.goldLight:T.grey,fontFamily:"'Outfit',sans-serif"}}>{opt.label.split(" ")[0]}</span>
                </button>
              ))}
            </div>
            {/* Card option */}
            <button onClick={()=>setPay("card")} style={{width:"100%",padding:"10px 14px",borderRadius:10,background:pay==="card"?T.goldDim:T.navyCard,border:`1.5px solid ${pay==="card"?T.goldBorder:T.glassBorder}`,cursor:"pointer",display:"flex",alignItems:"center",gap:10,transition:"all .15s"}}>
              <span style={{fontSize:16}}>💳</span>
              <span style={{fontSize:12,fontWeight:500,color:pay==="card"?T.goldLight:T.offwhite,fontFamily:"'Outfit',sans-serif"}}>Credit / Debit Card</span>
            </button>
            {pay==="card"&&(
              <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
                <input className="inp" placeholder="Card Number"/>
                <div style={{display:"flex",gap:8}}>
                  <input className="inp" placeholder="MM/YY" style={{flex:1}}/>
                  <input className="inp" placeholder="CVV" style={{flex:1}}/>
                </div>
                <input className="inp" placeholder="Name on Card"/>
              </div>
            )}
          </div>

          {/* Order summary — compact card matching Ticket Checkout */}
          <div style={{background:T.navyCard,border:`1px solid ${T.glassBorder}`,borderRadius:14,padding:"14px 16px",marginBottom:16}}>
            <div style={{fontSize:9,fontWeight:700,color:T.grey,letterSpacing:".12em",textTransform:"uppercase",marginBottom:10}}>Order Summary</div>
            {items.map((item,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:12}}>
                <span style={{color:T.offwhite}}>{item.name} × {item.qty}</span>
                <span style={{color:T.white,fontWeight:600}}>${(item.price*item.qty).toFixed(2)}</span>
              </div>
            ))}
            <div style={{height:1,background:T.glassBorder,margin:"8px 0"}}/>
            <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
              <span style={{color:T.grey}}>Subtotal</span>
              <span style={{color:T.grey}}>${sub.toFixed(2)}</span>
            </div>
            {promoOK&&(
              <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:11}}>
                <span style={{color:T.green}}>Promo (10% off)</span>
                <span style={{color:T.green}}>−${disc.toFixed(2)}</span>
              </div>
            )}
            <div style={{height:1,background:T.glassBorder,margin:"8px 0"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
              <span style={{fontSize:14,fontWeight:700,color:T.white}}>Total</span>
              <span style={{fontSize:18,fontWeight:800,color:T.gold}}>${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Terms */}
          <div style={{fontSize:9,color:T.grey,textAlign:"center",marginBottom:16,lineHeight:1.5}}>
            By proceeding to purchase, I agree to the{" "}
            <span style={{color:T.gold,textDecoration:"underline",cursor:"pointer"}}>ReelView Terms of Use</span>.
          </div>
        </div>
      </div>

      {/* Sticky footer — matching Ticket Checkout */}
      <div style={{flexShrink:0,borderTop:`1px solid ${T.glassBorder}`,background:T.navyGlass,backdropFilter:"blur(20px)",padding:"12px 16px 18px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey} strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <div>
              <div style={{fontSize:10,color:T.grey}}>{order?.totalItems || 0} item{(order?.totalItems||0)!==1?"s":""}</div>
              <div style={{fontSize:15,fontWeight:700,color:T.gold}}>${total.toFixed(2)}</div>
            </div>
          </div>
          <button className="btn-gold" style={{padding:"13px 28px",fontSize:14,borderRadius:50}} onClick={()=>nav("concessions-confirm")}>
            Purchase
          </button>
        </div>
      </div>
    </div>
  );
};

const ConcessionsConfirmScreen = ({ nav, order }) => {
  const code = "C-"+Math.random().toString(36).substr(2,6).toUpperCase();
  const items = order?.items || [];
  const sub = order?.totalAmt || 0;
  const pickupMovie = order?.pickupMovie;
  const pickupTime = order?.pickupTime || "At showtime";
  const theaterName = pickupMovie?.theater || "ReelView Grand Cineplex";
  const ptsEarned = Math.round(sub * 10);

  return (
    <div className="screen" style={{paddingBottom:40,background:T.navy}}>
      <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.glassBorder}`,textAlign:"center"}}><Logo size={0.9}/></div>
      <div style={{padding:"22px 16px"}}>

        {/* ── Success state ── */}
        <div style={{textAlign:"center",marginBottom:22}}>
          <div style={{width:64,height:64,borderRadius:"50%",background:"rgba(39,174,96,.15)",border:`2px solid ${T.green}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:26,animation:"scaleIn .4s ease"}}>🍿</div>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:T.white,marginBottom:4}}>Order Confirmed!</h2>
          <p style={{fontSize:12,color:T.grey}}>Show this screen at the concessions counter</p>
          <p style={{fontSize:10,color:T.gold,marginTop:4}}>+{ptsEarned} reward points earned on this order</p>
        </div>

        {/* ── Order Receipt Card ── */}
        <div style={{background:`linear-gradient(145deg,${T.navyLight},${T.navyMid})`,border:`1px solid ${T.goldBorder}`,borderRadius:20,padding:20,marginBottom:18,position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-28,right:-28,width:100,height:100,borderRadius:"50%",background:T.goldDim,pointerEvents:"none"}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
            <Logo size={0.72}/>
            <span className="tag">CONCESSIONS</span>
          </div>

          {/* Pickup details grid */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {[
              ["Pickup Time",pickupTime],
              ["Movie",pickupMovie?.title||"—"],
              ["Theater",theaterName],
              ["Items",`${order?.totalItems||items.length} item${(order?.totalItems||items.length)!==1?"s":""}`],
            ].map(([k,v])=>(
              <div key={k}><div style={{fontSize:9,color:T.grey,marginBottom:1,letterSpacing:".07em",textTransform:"uppercase"}}>{k}</div><div style={{fontSize:12,fontWeight:600,color:T.white}}>{v}</div></div>
            ))}
          </div>

          {/* Itemized order */}
          <div style={{borderTop:`1px solid rgba(255,255,255,.06)`,paddingTop:12,marginBottom:14}}>
            <div style={{fontSize:9,color:T.grey,letterSpacing:".08em",textTransform:"uppercase",marginBottom:8}}>ORDER DETAILS</div>
            {items.map((item,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:i<items.length-1?`1px solid rgba(255,255,255,.04)`:"none"}}>
                <div style={{width:30,height:30,borderRadius:6,overflow:"hidden",flexShrink:0,background:T.navyLight}}>
                  {CONCESSION_IMAGES[item.id] ? <img src={CONCESSION_IMAGES[item.id]} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}} loading="lazy"/> : <span style={{fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:"100%"}}>{item.emoji}</span>}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:T.white,fontWeight:500}}>{item.name}</div>
                  <div style={{fontSize:9,color:T.grey}}>Qty: {item.qty} × ${item.price.toFixed(2)}</div>
                </div>
                <span style={{fontSize:12,color:T.white,fontWeight:600}}>${(item.price*item.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Total */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",padding:"10px 0 2px",borderTop:`1px solid rgba(255,255,255,.08)`}}>
            <span style={{fontSize:14,fontWeight:700,color:T.white}}>Total Paid</span>
            <span style={{fontSize:16,fontWeight:700,color:T.gold}}>${sub.toFixed(2)}</span>
          </div>

          {/* Tear line */}
          <div style={{display:"flex",alignItems:"center",gap:0,margin:"16px -20px"}}>
            <div style={{width:18,height:18,borderRadius:"50%",background:T.navy,flexShrink:0}}/>
            <div style={{flex:1,borderTop:`2px dashed ${T.greyDim}`}}/>
            <div style={{width:18,height:18,borderRadius:"50%",background:T.navy,flexShrink:0}}/>
          </div>

          {/* Barcode + order number */}
          <div style={{textAlign:"center",paddingTop:2}}>
            <div style={{display:"flex",gap:1.5,justifyContent:"center",marginBottom:5}}>
              {Array.from({length:34},(_,i)=><div key={i} style={{width:i%5===0?3:i%3===0?2:1.5,height:40,background:T.white,borderRadius:1,opacity:.3+((i*17)%7)*.1}}/>)}
            </div>
            <div style={{fontSize:9,color:T.grey,letterSpacing:".18em"}}>{code}</div>
            <div style={{fontSize:8,color:T.grey,marginTop:2}}>Show this barcode at the counter</div>
          </div>
        </div>

        {/* ── Pickup Reminder ── */}
        <div style={{background:T.goldDim,border:`1px solid ${T.goldBorder}`,borderRadius:14,padding:14,marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
          <div style={{width:36,height:36,borderRadius:10,background:"rgba(201,168,76,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>⏱️</div>
          <div style={{flex:1}}>
            <div style={{fontSize:12,fontWeight:700,color:T.goldLight}}>Pickup Reminder</div>
            <div style={{fontSize:10,color:T.offwhite,marginTop:1,lineHeight:1.4}}>Head to {theaterName} concessions counter at <strong style={{color:T.goldLight}}>{pickupTime}</strong>. Your order will be ready!</div>
          </div>
        </div>

        {/* ── Add More / Back buttons ── */}
        <div style={{display:"flex",gap:10,marginBottom:12}}>
          <button className="btn-outline" style={{flex:1,padding:13,fontSize:13}} onClick={()=>nav("concessions")}>Order More</button>
          <button className="btn-gold" style={{flex:1,padding:13,fontSize:13}} onClick={()=>nav("movies")}>Back to Movies</button>
        </div>

      </div>
    </div>
  );
};

// (Invite Friends is now a modal inside TicketConfirmScreen)

// ══════════════════════════════════════════════════════
// ROOT
// ══════════════════════════════════════════════════════
const NAV_ACTIVE = {
  movies:["movies","movie-detail","seat-map","ticket-qty","ticket-checkout","ticket-confirm"],
  theaters:["theaters","theater-detail"],
  concessions:["concessions","concessions-checkout","concessions-confirm"],
  myreelview:["myreelview"],
};

export default function App() {
  const [screen, setScreen] = useState("movies");
  const [movie, setMovie] = useState(null);
  const [theater, setTheater] = useState(null);
  const [showtime, setShowtime] = useState(null);
  const [seatData, setSeatData] = useState(null);
  const [ticketOrder, setTicketOrder] = useState(null);
  const [concessionOrder, setConcessionOrder] = useState(null); // {cart, pickupMovie, pickupTime, totalAmt, totalItems}
  const [theaterInitTab, setTheaterInitTab] = useState("showtimes");
  const [seatTimer, setSeatTimer] = useState(null); // null = not started, number = seconds left
  const [prevScreen, setPrevScreen] = useState(null); // track where user came from
  const [favs, setFavs] = useState([1,4]);
  const toggleFav = (e, id) => { e.stopPropagation(); setFavs(f=>f.includes(id)?f.filter(x=>x!==id):[...f,id]); };

  // Persistent seat-hold timer — ticks across all transaction screens
  const TIMER_SCREENS = ["seat-map","ticket-qty","ticket-checkout"];
  useEffect(() => {
    if (seatTimer === null || seatTimer <= 0) return;
    if (!TIMER_SCREENS.includes(screen)) return; // only tick on transaction screens
    const id = setInterval(() => setSeatTimer(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(id);
  }, [seatTimer, screen]);

  const nav = s => { setPrevScreen(screen); setScreen(s); };
  const activeNav = Object.entries(NAV_ACTIVE).find(([,screens])=>screens.includes(screen))?.[0]||"movies";
  const hideBottomNav = ["ticket-checkout","ticket-confirm","concessions-confirm"].includes(screen);

  return (
    <div style={{display:"flex",justifyContent:"center",alignItems:"flex-start",minHeight:"100vh",background:"#020408",padding:"28px 16px",position:"relative"}}>
      <FontStyle/>
      {/* Phone shell — flex column so screens + nav stack vertically */}
      <div style={{width:360,height:812,background:`linear-gradient(180deg,${T.navy} 0%,#020508 100%)`,borderRadius:44,overflow:"hidden",position:"relative",boxShadow:"0 60px 140px rgba(0,0,0,.85),0 0 0 1px rgba(255,255,255,.06),inset 0 1px 0 rgba(255,255,255,.04)",border:"8px solid #0a0a0a",display:"flex",flexDirection:"column"}}>
        {/* Screen glow — ambient projector light hitting the ceiling */}
        <div style={{position:"absolute",top:0,left:0,right:0,height:280,background:"radial-gradient(ellipse at 50% -20%,rgba(201,168,76,0.05) 0%,rgba(201,168,76,0.02) 40%,transparent 70%)",pointerEvents:"none",zIndex:1}}/>
        {/* Film grain overlay */}
        <div style={{position:"absolute",inset:0,backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E\")",opacity:.5,pointerEvents:"none",zIndex:2,mixBlendMode:"overlay",animation:"grainDrift 8s steps(10) infinite"}}/>
        {/* Notch */}
        <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:110,height:26,background:"#0a0a0a",borderRadius:"0 0 16px 16px",zIndex:300,flexShrink:0}}/>
        {/* Notch spacer */}
        <div style={{height:26,flexShrink:0}}/>
        {/* Screens area — flex:1 so it fills remaining space, overflow hidden so each screen manages its own scroll */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",position:"relative",zIndex:3}}>
          {screen==="movies" && <MoviesScreen nav={nav} setMovie={setMovie} favs={favs} toggleFav={toggleFav}/>}
          {screen==="movie-detail" && <MovieDetailScreen movie={movie} nav={nav} setShowtime={setShowtime} setTheater={setTheater} setTheaterInitTab={setTheaterInitTab} favs={favs} toggleFav={toggleFav}/>}
          {screen==="seat-map" && <SeatMapScreen showtime={showtime} nav={nav} setSeatData={setSeatData} seatTimer={seatTimer} setSeatTimer={setSeatTimer}/>}
          {screen==="ticket-qty" && <TicketQtyScreen showtime={showtime} seats={seatData} nav={nav} setTicketOrder={setTicketOrder} seatTimer={seatTimer}/>}
          {screen==="ticket-checkout" && <TicketCheckoutScreen showtime={showtime} seats={seatData} ticketOrder={ticketOrder} nav={nav} seatTimer={seatTimer}/>}
          {screen==="ticket-confirm" && <TicketConfirmScreen showtime={showtime} seats={seatData} nav={nav} prevScreen={prevScreen}/>}
          {screen==="theaters" && <TheatersScreen nav={nav} setTheater={setTheater} setTheaterInitTab={setTheaterInitTab}/>}
          {screen==="theater-detail" && <TheaterDetailScreen theater={theater} nav={nav} setMovie={setMovie} setShowtime={setShowtime} initTab={theaterInitTab}/>}
          {screen==="myreelview" && <MyReelViewScreen nav={nav} setShowtime={setShowtime} setSeatData={setSeatData}/>}
          {screen==="concessions" && <ConcessionsScreen nav={nav} setConcessionOrder={setConcessionOrder}/>}
          {screen==="concessions-checkout" && <ConcessionsCheckoutScreen nav={nav} order={concessionOrder}/>}
          {screen==="concessions-confirm" && <ConcessionsConfirmScreen nav={nav} order={concessionOrder}/>}
        </div>
        {/* Bottom nav — structural, always at bottom of phone */}
        {!hideBottomNav && <BottomNav active={activeNav} nav={nav}/>}
      </div>
      {/* Screen label */}
      <div style={{position:"fixed",bottom:16,left:"50%",transform:"translateX(-50%)",background:"rgba(4,8,15,.75)",backdropFilter:"blur(12px)",border:`1px solid ${T.glassBorder}`,borderRadius:20,padding:"5px 14px",fontSize:10,color:T.grey,letterSpacing:".06em",textTransform:"uppercase",fontFamily:"'Outfit',sans-serif"}}>{screen.replace(/-/g," ")}</div>
    </div>
  );
}
